import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import EChartRenderer from './EChartRenderer';
import UPSELLerateLogo from './UPSELLerate-logo';
import Dashboard from './Dashboard';
import Register from './pages/Register';
import Integrations from './pages/Integrations';
import CalendarioCampanas from './pages/CalendarioCampanas';
import Settings from './pages/Settings';
import GoldenOpportunities from './pages/GoldenOpportunities';
import RevenueFinder from './pages/RevenueFinder';
import RevenueCheckpoint from './pages/RevenueCheckpoint';
import ImpactPage from './pages/Impact';
import MyPlays from './pages/MyPlays';
import EmailBranding from './pages/EmailBranding';
import FloatingChatButton from './components/FloatingChatButton';
import ShopifyComplete from './pages/ShopifyComplete';
import ShopifyDemo from './pages/ShopifyDemo';
import PlaysView from './components/Plays/PlaysView';
import PlaysDashboard from './components/Plays/PlaysDashboard';
import AdminAccounts from './components/Plays/AdminAccounts';
import { useI18n } from './context/i18n';
import { dedupFetch } from './utils/dedupFetch';
import './App.css';
import './feedback-styles.css';
import './thinking-styles.css';
import './markdown-styles.css';
import logoUpsellerate from './assets/logo-upsellerate-v2.png';

// Feature flag: el UI de Billing (boton nav + pricing page + retorno /billing) queda
// OCULTO en produccion hasta que el backend de billing este desplegado en prod y
// Alejo decida lanzar precios. Habilitar con REACT_APP_ENABLE_BILLING=true (local/test).
const BILLING_UI_ENABLED = process.env.REACT_APP_ENABLE_BILLING === 'true';

// Feature flags for nav-restructure (2026-07-18). Both default OFF.
// calendar_enabled: when ON, "Calendar" shows between Revenue Impact and Settings in nav.
const CALENDAR_ENABLED = process.env.REACT_APP_CALENDAR_ENABLED === 'true';
// floating_chat: when ON, a floating chat launcher button appears globally (shell only for now).
const FLOATING_CHAT_ENABLED = process.env.REACT_APP_FLOATING_CHAT === 'true';

// Canonical nav views (product US-first, English chrome, sentence case).
// Maps internal `currentView` state <-> URL path so bookmarks/deep links work.
const VIEW_TO_PATH = {
  checkpoint: '/checkpoint',
  revenue: '/plays',
  impact: '/impact',
  myplays: '/my-plays',
  branding: '/branding',
  settings: '/settings',
  dashboard: '/dashboard',
  chat: '/chat',
  integrations: '/integrations',
  calendar: '/calendar',
  pricing: '/pricing',
  opportunities: '/plays',
};

// Old/legacy paths (bookmarks, Shopify deep links) redirect to the new canonical view.
// /impact fused into My Plays (control + results in one page, 2026-08-02).
const PATH_TO_VIEW = {
  '/checkpoint': 'checkpoint',
  '/plays': 'revenue',
  '/opportunities': 'revenue', // legacy label "Oportunidades"
  '/revenue': 'revenue',
  '/impact': 'myplays',
  '/my-plays': 'myplays',
  '/settings': 'settings',
  '/configuracion': 'settings', // legacy Spanish label
  '/dashboard': 'dashboard',
  '/chat': 'revenue', // Chat left the nav; deep links land where it's embedded (Revenue Plays)
  '/integrations': 'settings',
  '/calendar': CALENDAR_ENABLED ? 'calendar' : 'myplays',
  '/calendario': CALENDAR_ENABLED ? 'calendar' : 'myplays', // legacy Spanish label
  // Billing deep links only resolve when the Billing UI flag is ON; with the
  // flag OFF these paths are unknown and fall through to the readiness routing.
  // NOTE: '/plays' is intentionally NOT remapped to 'plays' here (kept as
  // 'revenue' above) — the standalone PlaysDashboard view is retired from nav
  // in favor of My Plays; the key stays for any code still referencing it.
  ...(BILLING_UI_ENABLED ? { '/pricing': 'pricing', '/billing': 'pricing' } : {}),
};

// Componentes de iconos SVG
const ThumbsUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 7v6.5c0 .28.22.5.5.5h1c.28 0 .5-.22.5-.5V7H4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6 7V4.5c0-.83.67-1.5 1.5-1.5.55 0 1 .45 1 1v2l1.5 2h2.5c.83 0 1.5.67 1.5 1.5 0 .17-.03.33-.08.48l-1.17 3.5c-.14.43-.54.73-.99.73H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ThumbsDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 9V2.5c0-.28.22-.5.5-.5h1c.28 0 .5.22.5.5V9H4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6 9v2.5c0 .83.67 1.5 1.5 1.5.55 0 1-.45 1-1V10l1.5-2h2.5c.83 0 1.5-.67 1.5-1.5 0-.17-.03-.33-.08-.48l-1.17-3.5c-.14-.43-.54-.73-.99-.73H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BotIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="6" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="8" cy="10" r="1" fill="currentColor"/>
    <circle cx="12" cy="10" r="1" fill="currentColor"/>
    <path d="M7 13h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M10 6V4M10 4L8 2M10 4L12 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4 9H3M16 9h1M4 12H3M16 12h1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// URLs del backend - detección automática según entorno
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const API_URL = isLocalhost 
  ? 'http://localhost:8080'  // Local development - Backend principal
  : 'https://mi-chat-892931479853.us-central1.run.app';  // Production

const AUTH_URL = 'https://primeval-gear-417120.ue.r.appspot.com';  // Siempre producción
const SHOPIFY_AUTH_URL = process.env.REACT_APP_SHOPIFY_AUTH_URL || 'https://auth.upsellerate.co';
const DASHBOARD_API_URL = 'https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com';

console.log(`[ENV] Modo: ${isLocalhost ? 'LOCAL' : 'PRODUCCIÓN'}`);
console.log(`[ENV] API_URL: ${API_URL}`);

// --- Dedup de GET /auth/shopify/sync-status -----------------------------------
// Al entrar con contexto Shopify se montan dos useEffect de polling que disparan
// una llamada inmediata cada uno (dos requests con segundos de diferencia). Este
// helper colapsa llamadas concurrentes/casi-simultaneas al MISMO endpoint en un
// solo request de red: reusa la promesa en vuelo y, una vez resuelta, reusa el
// Response clonado durante una ventana corta. NO afecta el polling periodico
// (cada intervalo genera un request nuevo tras vencer la ventana).
const _syncStatusInflight = new Map(); // key -> { promise, ts }
const SYNC_STATUS_DEDUP_WINDOW_MS = 2000;

// Dedup key = pathname del endpoint (ignora query). Los dos useEffect de polling
// mandan query strings distintas (uno con shop, otro sin) pero apuntan al mismo
// tenant/endpoint; sin normalizar la key no se colapsarian. El backend igual
// cachea por company:dataset:shop, asi que reusar la respuesta es correcto.
const _syncStatusKey = (url) => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch (e) {
    return url.split('?')[0];
  }
};

// NOTA (revisor): la dedup por pathname hace que distintos call sites compartan
// la MISMA respuesta aunque pidan query strings distintas. Esto depende de que el
// backend devuelva la UNION de campos en ambas ramas del endpoint (counts +
// progress/full_backfill_status en running y no-running), como hace hoy. Si una
// rama dejara de incluir campos que un call site necesita, revisar esta dedup.
const fetchSyncStatusDeduped = (url, options) => {
  const now = Date.now();
  const key = _syncStatusKey(url);
  const existing = _syncStatusInflight.get(key);
  if (existing && now - existing.ts < SYNC_STATUS_DEDUP_WINDOW_MS) {
    return existing.promise.then((res) => res.clone());
  }
  const promise = fetch(url, options);
  _syncStatusInflight.set(key, { promise, ts: now });
  promise
    .then((res) => {
      // conservar la respuesta resuelta durante la ventana para reuso
      _syncStatusInflight.set(key, { promise: Promise.resolve(res.clone()), ts: Date.now() });
    })
    .catch(() => {
      _syncStatusInflight.delete(key);
    });
  return promise.then((res) => res.clone());
};

const routeShopifyUserAfterOnboarding = async (token, setCurrentView) => {
  try {
    if (!token) return setCurrentView('dashboard');

    const headers = { Authorization: `Bearer ${token}` };

    const response = await fetchSyncStatusDeduped(`${SHOPIFY_AUTH_URL}/auth/shopify/sync-status`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) return setCurrentView('dashboard');

    const data = await response.json();
    const baseReady =
      data?.full_backfill_status === 'complete' &&
      data?.ready === true;

    if (!baseReady) return setCurrentView('dashboard');

    const companyId = data?.company_id || localStorage.getItem('companyId') || localStorage.getItem('shopifyCompanyId');
    if (!companyId) return setCurrentView('dashboard');

    const [healthRes, summaryRes] = await Promise.all([
      fetch(`${DASHBOARD_API_URL}/api/dashboard/checkpoint/health-score?company=${encodeURIComponent(companyId)}`, { headers }),
      // dedupFetch: RevenueCheckpoint (vista destino de este routing) vuelve a
      // pedir este mismo endpoint al montar — colapsar en un solo request.
      dedupFetch(`${DASHBOARD_API_URL}/revenue-finder/summary?company=${encodeURIComponent(companyId)}`, { headers }),
    ]);

    const insightsReady = healthRes.ok && summaryRes.ok;
    setCurrentView(insightsReady ? 'checkpoint' : 'dashboard');
  } catch (error) {
    console.warn('Shopify routing check failed:', error);
    setCurrentView('dashboard');
  }
};


const shopifyProgressMessages = [
  'Connecting securely to Shopify',
  'Creating your UPSELLerate workspace',
  'Importing customers',
  'Importing orders',
  'Importing products',
  'Preparing your dashboard',
];

function getStoredShopifyInitialSync() {
  try {
    return JSON.parse(localStorage.getItem('shopifyInitialSync') || 'null');
  } catch (error) {
    return null;
  }
}

// FIX onboarding (2026-08-04): al instalar la app en Shopify por primera vez
// (incognito) la primerisima carga puede resolver SIN token en localStorage
// todavia (el canje del OTT en /shopify/complete lo guarda un instante despues,
// o la app aterriza en '/' con el contexto de Shopify antes de que exista token).
// En ese caso NO debemos mostrar el login: hay una instalacion en curso. Este
// helper detecta ese estado "instalacion Shopify en progreso pero sin token aun"
// mirando (a) params de la URL propios del handoff de Shopify (ott/shop/host/
// hmac/company_id/dataset_name) y (b) el handoff persistido shopifyInitialSync.
// El login SOLO debe aparecer cuando NO hay token NI instalacion en curso.
function hasShopifyInstallInProgress() {
  try {
    const params = new URLSearchParams(window.location.search);
    const urlSignals = ['ott', 'shop', 'host', 'hmac', 'company_id', 'dataset_name'];
    if (urlSignals.some((k) => params.get(k))) return true;
    // Handoff del OAuth persistido (sync inicial arrancado tras el exchange).
    if (getStoredShopifyInitialSync()) return true;
    return false;
  } catch (error) {
    return false;
  }
}

function hasCompletedShopifySync(data) {
  if (!data) return false;
  const status = String(data.status || '').toLowerCase();

  if (data.already_synced === true) {
    return true;
  }

  const counts = data.counts || data.result || data;
  const hasBaseTablesReady =
    Number(counts?.customers || 0) > 0 &&
    Number(counts?.orders || 0) > 0 &&
    Number(counts?.products || 0) > 0;

  return (
    hasBaseTablesReady &&
    (
      status === 'complete' ||
      status === 'completed' ||
      status === 'success' ||
      data.complete === true ||
      data.completed === true ||
      data.ready === true ||
      data.has_data === true ||
      data.data_ready === true
    )
  );
}

const getShopifyEntryUiRequestId = () => {
  const key = 'shopifyEntryUiRequestId';
  let requestId = sessionStorage.getItem(key);
  if (!requestId) {
    requestId = Math.random().toString(16).slice(2, 10);
    sessionStorage.setItem(key, requestId);
  }
  return requestId;
};

const shopifyEntryUiDurationMs = (start) => Math.round((performance.now() - start) * 10) / 10;

const logShopifyEntryUi = ({
  request_id,
  shop,
  company_id,
  dataset_name,
  stage,
  status,
  duration_ms,
  ...fields
}) => {
  const parts = [
    '[shopify-entry-ui]',
    `request_id=${request_id || '-'}`,
    `shop=${shop || '-'}`,
    `company_id=${company_id || '-'}`,
    `dataset=${dataset_name || '-'}`,
    `stage=${stage}`,
    `status=${status}`,
  ];
  if (duration_ms !== undefined) parts.push(`duration_ms=${duration_ms}`);
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) parts.push(`${key}=${value}`);
  });
  console.log(parts.join(' '));
};

// Componente memoizado para renderizar Markdown y evitar re-renderizados al escribir
const MessageContent = React.memo(({ text }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Enlaces - detectar descargas CSV
        a: ({node, children, href, ...props}) => {
          // Detectar enlaces de descarga CSV
          if (href && href.startsWith('/download-csv/')) {
            const csvId = href.replace('/download-csv/', '');
            const downloadUrl = `${API_URL}/download-csv/${csvId}`;
            const filename = children && children.length > 0 ? String(children) : 'export.csv';
            
            const handleDownload = async (e) => {
              e.preventDefault();
              try {
                console.log(`[CSV] Descargando desde: ${downloadUrl}`);
                const response = await fetch(downloadUrl);
                if (!response.ok) {
                  throw new Error(`Error ${response.status}: ${response.statusText}`);
                }
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                console.log(`[CSV] Descarga completada: ${filename}`);
              } catch (error) {
                console.error('[CSV] Error al descargar:', error);
                alert(`Error downloading the file: ${error.message}`);
              }
            };
            
            return (
              <button 
                onClick={handleDownload}
                className="csv-download-button"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  textDecoration: 'none',
                  margin: '8px 0'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{marginRight: '8px'}}>
                  <path d="M8 11L4 7h2.5V3h3v4H12L8 11z" fill="currentColor"/>
                  <path d="M2 13h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                📥 {filename}
              </button>
            );
          }
          
          // Enlaces normales
          const content = children && children.length > 0 ? children : (href || 'link');
          return <a href={href} {...props} target="_blank" rel="noreferrer">{content}</a>;
        },
        // Código inline y bloques
        code: ({node, inline, className, children, ...props}) => {
          return inline ? (
            <code className="inline-code" {...props}>{children}</code>
          ) : (
            <code className="code-block" {...props}>{children}</code>
          );
        },
        // Tablas
        table: ({node, ...props}) => (
          <div className="markdown-table-wrapper">
            <table className="markdown-table" {...props} />
          </div>
        ),
        thead: ({node, ...props}) => <thead className="markdown-thead" {...props} />,
        tbody: ({node, ...props}) => <tbody className="markdown-tbody" {...props} />,
        tr: ({node, ...props}) => <tr className="markdown-tr" {...props} />,
        th: ({node, ...props}) => <th className="markdown-th" {...props} />,
        td: ({node, ...props}) => <td className="markdown-td" {...props} />,
        // Listas
        ul: ({node, ...props}) => <ul className="markdown-ul" {...props} />,
        ol: ({node, ...props}) => <ol className="markdown-ol" {...props} />,
        li: ({node, ...props}) => <li className="markdown-li" {...props} />,
        // Citas
        blockquote: ({node, ...props}) => <blockquote className="markdown-blockquote" {...props} />,
        // Títulos
        h1: ({node, ...props}) => <h1 className="markdown-h1" {...props} />,
        h2: ({node, ...props}) => <h2 className="markdown-h2" {...props} />,
        h3: ({node, ...props}) => <h3 className="markdown-h3" {...props} />,
        h4: ({node, ...props}) => <h4 className="markdown-h4" {...props} />,
        // Párrafos
        p: ({node, ...props}) => <p className="markdown-p" {...props} />,
        // Líneas horizontales
        hr: ({node, ...props}) => <hr className="markdown-hr" {...props} />,
        // Negritas y cursivas
        strong: ({node, ...props}) => <strong className="markdown-strong" {...props} />,
        em: ({node, ...props}) => <em className="markdown-em" {...props} />,
      }}
    >
      {String(text || '')}
    </ReactMarkdown>
  );
});

const appTranslations = {
  es: {
    loginTitle: 'Asistente de Datos Inteligente',
    loginSubtitle: 'Inicia sesión para acceder a tus análisis',
    username: 'Usuario',
    usernamePlaceholder: 'Ingresa tu usuario',
    password: 'Contraseña',
    passwordPlaceholder: 'Ingresa tu contraseña',
    signingIn: 'Iniciando...',
    signIn: 'Iniciar Sesión',
    noAccount: '¿No tienes una cuenta?',
    register: 'Regístrate',
    newConversation: 'Nueva conversación',
    assistantChat: 'Chat Asistente',
    integrations: 'Integraciones',
    conversations: 'Conversaciones',
    sessionTitle: 'Sesión',
    today: 'Hoy',
    yesterday: 'Ayer',
    previous: 'Anteriores',
    deleteConversation: 'Eliminar conversación',
    hideMetrics: 'Ocultar métricas',
    showMetrics: 'Mostrar métricas',
    lightMode: 'Modo claro',
    darkMode: 'Modo oscuro',
    logout: 'Salir',
    totalTokens: 'Total Tokens',
    costUsd: 'Costo USD',
    llmCalls: 'Llamadas LLM',
    tools: 'Herramientas',
    welcomeTitle: '¿En qué puedo ayudarte hoy?',
    welcomeSubtitle: 'Pregunta sobre tus datos, crea segmentos o genera reportes',
    suggestionSalesPrompt: '¿Cuáles fueron las ventas del último mes?',
    suggestionSegmentPrompt: 'Crea un segmento de clientes activos',
    suggestionTopPrompt: '¿Cuáles son los productos más vendidos?',
    suggestionSalesLabel: 'Ventas del último mes',
    suggestionSegmentLabel: 'Crear segmento',
    suggestionTopLabel: 'Top productos',
    showSql: 'Ver query SQL',
    hideSql: 'Ocultar query SQL',
    sqlUsed: 'Query SQL utilizada',
    copyQuery: 'Copiar query',
    copy: 'Copiar',
    goodAnswer: 'Buena respuesta',
    badAnswer: 'Mala respuesta',
    messagePlaceholder: 'Escribe tu mensaje...',
    startSessionPlaceholder: 'Inicia una sesión para empezar',
    enterHint: 'Presiona Enter para enviar, Shift+Enter para nueva línea',
    feedbackTitle: 'Proporciona feedback adicional',
    feedbackIssueQuestion: '¿Qué tipo de problema encontraste? (opcional)',
    feedbackDetailQuestion: 'Proporciona detalles adicionales (opcional)',
    feedbackPlaceholder: '¿Qué podríamos mejorar en esta respuesta?',
    cancel: 'Cancelar',
    sendFeedback: 'Enviar feedback'
  },
  en: {
    loginTitle: 'Intelligent Data Assistant',
    loginSubtitle: 'Sign in to access your analytics',
    username: 'Username',
    usernamePlaceholder: 'Enter your username',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    signingIn: 'Signing in...',
    signIn: 'Sign In',
    noAccount: 'Don\'t have an account?',
    register: 'Register',
    newConversation: 'New conversation',
    assistantChat: 'Assistant Chat',
    integrations: 'Integrations',
    conversations: 'Conversations',
    sessionTitle: 'Session',
    today: 'Today',
    yesterday: 'Yesterday',
    previous: 'Previous',
    deleteConversation: 'Delete conversation',
    hideMetrics: 'Hide metrics',
    showMetrics: 'Show metrics',
    lightMode: 'Light mode',
    darkMode: 'Dark mode',
    logout: 'Logout',
    totalTokens: 'Total Tokens',
    costUsd: 'USD Cost',
    llmCalls: 'LLM Calls',
    tools: 'Tools',
    welcomeTitle: 'How can I help you today?',
    welcomeSubtitle: 'Ask about your data, create segments, or generate reports',
    suggestionSalesPrompt: 'What were the sales in the last month?',
    suggestionSegmentPrompt: 'Create a segment of active customers',
    suggestionTopPrompt: 'What are the best-selling products?',
    suggestionSalesLabel: 'Last month sales',
    suggestionSegmentLabel: 'Create segment',
    suggestionTopLabel: 'Top products',
    showSql: 'Show SQL query',
    hideSql: 'Hide SQL query',
    sqlUsed: 'Used SQL query',
    copyQuery: 'Copy query',
    copy: 'Copy',
    goodAnswer: 'Good answer',
    badAnswer: 'Bad answer',
    messagePlaceholder: 'Type your message...',
    startSessionPlaceholder: 'Start a session to begin',
    enterHint: 'Press Enter to send, Shift+Enter for new line',
    feedbackTitle: 'Provide additional feedback',
    feedbackIssueQuestion: 'What kind of issue did you find? (optional)',
    feedbackDetailQuestion: 'Provide additional details (optional)',
    feedbackPlaceholder: 'What could we improve in this answer?',
    cancel: 'Cancel',
    sendFeedback: 'Send feedback'
  }
};


// Force light mode on initial load
document.documentElement.className = "light-mode";

function App() {
  const { language, setLanguage, t } = useI18n();
  const tx = (key, fallback = '') => t(appTranslations, key, fallback);
  const navigate = useNavigate();
  const location = useLocation();

  const isCertificationMode = false;

  // Estado para la autenticación
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(localStorage.getItem('token'));
  // WATCHDOG del loader "Connecting to Shopify..." (2026-08-04): si la app queda
  // sin token con una instalacion Shopify en curso y el token NO llega en ~18s,
  // caemos al login normal en vez de dejar el spinner infinito. true => se ignora
  // hasShopifyInstallInProgress() y se muestra el login.
  const [shopifyHandoffTimedOut, setShopifyHandoffTimedOut] = useState(false);

  // Arranca el watchdog SOLO cuando no hay token pero hay install en curso.
  // Si el token llega antes, se limpia el timeout (happy path: nunca dispara).
  useEffect(() => {
    if (token) {
      // El token llego: reset por si acaso y no armar watchdog.
      if (shopifyHandoffTimedOut) setShopifyHandoffTimedOut(false);
      return undefined;
    }
    if (!hasShopifyInstallInProgress()) return undefined;
    const timer = window.setTimeout(() => {
      setShopifyHandoffTimedOut(true);
    }, 18000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Estado de navegación (chat o dashboard)
  const [currentView, setCurrentView] = useState(null); // null | 'chat' | 'dashboard' | 'checkpoint' | 'integrations' | 'revenue' | 'settings' | 'calendar'

  // Keep the URL in sync with currentView so bookmarks/deep links work.
  //
  // Mount: record the requested view from the URL in deepLinkViewRef WITHOUT
  // setting currentView directly — the pre-readiness gate
  // (routeShopifyUserAfterOnboarding, effect further down) must run first; it
  // applies the deep-linked view only when the account is ready, otherwise the
  // user lands on Dashboard with the sync banners as always.
  // The Shopify Billing checkout return (/billing?status=...&plan=...) is
  // explicitly skipped here: its own effect below reads those params from the
  // URL, so we must not navigate away before it runs.
  const deepLinkViewRef = useRef(null);
  useEffect(() => {
    const path = location.pathname;
    if (path === '/billing') return; // handled by the billing checkout effect
    const mappedView = PATH_TO_VIEW[path];
    if (!mappedView) return;
    const canonicalPath = VIEW_TO_PATH[mappedView];
    if (canonicalPath && canonicalPath !== path) {
      navigate(canonicalPath, { replace: true });
    }
    deepLinkViewRef.current = mappedView;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // View -> URL: push a history entry for each view change so back/forward work.
  useEffect(() => {
    if (!currentView) return;
    const targetPath = VIEW_TO_PATH[currentView];
    if (targetPath && targetPath !== location.pathname) {
      navigate(targetPath, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]);

  // URL -> view: when the path changes underneath us (back/forward), update the
  // view. Skipped while currentView is null (pre-readiness routing owns that
  // phase) and when the current view's canonical path already matches (avoids
  // fighting legacy aliases like /chat -> revenue or /integrations -> settings).
  useEffect(() => {
    if (currentView === null) return;
    if (VIEW_TO_PATH[currentView] === location.pathname) return;
    const mappedView = PATH_TO_VIEW[location.pathname];
    if (!mappedView || mappedView === currentView) return;
    setCurrentView(mappedView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Estado de contexto de datos (por usuario) - cargar desde localStorage
  const [projectId, setProjectId] = useState(localStorage.getItem('projectId') || '');
  const [datasetId, setDatasetId] = useState(localStorage.getItem('datasetId') || '');
  const [companyInfo, setCompanyInfo] = useState(null);

  // Estado para el chat
  const [sessionId, setSessionId] = useState(localStorage.getItem('sessionId') || null);
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Estado para historial de conversaciones (ahora desde backend)
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(localStorage.getItem('currentConversationId') || null);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  
  // Cache de mensajes de conversaciones ya cargadas
  const [conversationCache, setConversationCache] = useState({});

  // Estado para métricas de tokens
  const [metrics, setMetrics] = useState(null);
  const [showMetricsPanel, setShowMetricsPanel] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [darkMode, setDarkMode] = useState(false);

  const clearStoredSessionContext = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('companyId');
    localStorage.removeItem('datasetId');
    localStorage.removeItem('projectId');
    localStorage.removeItem('shopifyCompanyId');
    localStorage.removeItem('shopifyShop');
    localStorage.removeItem('shopifyInitialSync');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('displayName');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('currentConversationId');
    localStorage.removeItem('companyInfo');
  };

  const resetAuthenticatedContextState = () => {
    setCompanyInfo(null);
    setDatasetId(null);
    setProjectId('primeval-gear-417120');
    setSessionId(null);
    setCurrentConversationId(null);
    setChatHistory([]);
  };
  
  // Estado para feedback de mensajes
  const [messageFeedback, setMessageFeedback] = useState({}); // {messageIndex: 'positive' | 'negative' | null}
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  
  // Estado para SQL queries expandidas
  const [expandedQueries, setExpandedQueries] = useState({}); // {messageIndex: boolean}
  
  // Estado para mensajes de "thinking" (razonamiento del agente)
  const [thinkingMessages, setThinkingMessages] = useState([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackModalData, setFeedbackModalData] = useState({ messageIndex: null, feedbackText: '', selectedIssues: [] });
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false); // Prevenir doble envío
  
  // Ref para prevenir doble envío de mensajes (independiente del renderizado)
  const isSendingRef = useRef(false);
  const shopifySyncStartedRef = useRef(null);
  const shopifySyncStateRef = useRef(null);
  const shopifySetupRenderedRef = useRef(null);
  const shopifyDashboardReloadRef = useRef({ key: null, count: 0 });
  const [shopifyInitialSync, setShopifyInitialSync] = useState(getStoredShopifyInitialSync);
  const [shopifyProgressIndex, setShopifyProgressIndex] = useState(0);
  const backfillStatusRef = useRef('unknown');
  const [backfillState, setBackfillState] = useState({ status: null, hasData: false });
  // FIX (hueco de banners 2026-08-09): true SOLO cuando el sync inicial pasa a
  // 'complete' DURANTE esta sesion montada (no cuando ya venia 'complete' de
  // localStorage al cargar la pagina — eso es una tienda vieja/login normal).
  // Se usa para (a) mantener el banner grande visible en modo "handoff" hasta
  // que el backfill reporte un estado visible, y (b) acelerar el poll del
  // backfill a 5s durante ese handoff, en vez de esperar hasta 15s.
  const shopifySyncJustCompletedRef = useRef(false);
  const shopifySyncCompletedAtRef = useRef(null);
  const [dashboardReloadKey, setDashboardReloadKey] = useState(0);
  // Resultado del retorno de Shopify Billing checkout. El backend redirige a
  // `${FRONTEND_URL}/billing?status=active|error|reauth&plan=...` (billing/routes.py),
  // pero la SPA navega por currentView, asi que capturamos la URL al montar.
  const [billingCheckoutResult, setBillingCheckoutResult] = useState(null); // null | {status, plan}
  // true cuando esta carga viene del returnUrl de Shopify Billing: evita que el
  // routing post-onboarding pise la vista 'pricing' al restaurar la sesion.
  const billingReturnRef = useRef(window.location.pathname === '/billing');

  useEffect(() => {
    if (!BILLING_UI_ENABLED) return;
    if (window.location.pathname !== '/billing') return;
    const params = new URLSearchParams(window.location.search);
    setBillingCheckoutResult({
      status: params.get('status') || 'unknown',
      plan: params.get('plan') || null,
    });
    setCurrentView('pricing');
    // Limpiar la URL para no re-disparar el flujo en reloads
    window.history.replaceState({}, '', '/');
  }, []);

  // Pantalla admin interna (sin boton de navegacion): solo accesible
  // navegando explicitamente a /admin. El componente pide su propio
  // BILLING_ADMIN_TOKEN, asi que no expone nada por si solo. Ungated a
  // proposito: es una herramienta interna, no depende del flag de UI.
  const adminReturnRef = useRef(window.location.pathname === '/admin');
  useEffect(() => {
    if (!adminReturnRef.current) return;
    setCurrentView('admin');
    window.history.replaceState({}, '', '/');
  }, []);

  useEffect(() => {
    shopifySyncStateRef.current = shopifyInitialSync;
    if (shopifyInitialSync) {
      logShopifyEntryUi({
        request_id: getShopifyEntryUiRequestId(),
        shop: shopifyInitialSync.shop,
        company_id: shopifyInitialSync.company_id,
        dataset_name: shopifyInitialSync.dataset_name,
        stage: 'initial_sync_state_seen',
        status: shopifyInitialSync.status || 'unknown',
      });
    }
  }, [shopifyInitialSync]);

  useEffect(() => {
    if (
      !token ||
      currentView !== 'dashboard' ||
      !shopifyInitialSync ||
      !['pending', 'running'].includes(shopifyInitialSync.status)
    ) {
      return undefined;
    }

    const syncKey = `${shopifyInitialSync.company_id || ''}:${shopifyInitialSync.dataset_name || ''}:${shopifyInitialSync.shop || ''}`;

    if (shopifyDashboardReloadRef.current.key !== syncKey) {
      shopifyDashboardReloadRef.current = { key: syncKey, count: 0 };
    }

    if (shopifyDashboardReloadRef.current.count >= 6) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      const latest = shopifySyncStateRef.current || {};

      if (!['pending', 'running'].includes(latest.status)) {
        window.clearInterval(intervalId);
        return;
      }

      if (shopifyDashboardReloadRef.current.count >= 6) {
        window.clearInterval(intervalId);
        return;
      }

      shopifyDashboardReloadRef.current.count += 1;
      setDashboardReloadKey((current) => current + 1);

      logShopifyEntryUi({
        request_id: getShopifyEntryUiRequestId(),
        shop: latest.shop,
        company_id: latest.company_id,
        dataset_name: latest.dataset_name,
        stage: 'dashboard_auto_reload',
        status: latest.status || 'unknown',
        attempt: shopifyDashboardReloadRef.current.count,
      });
    }, 15000);

    return () => window.clearInterval(intervalId);
  }, [
    token,
    currentView,
    shopifyInitialSync?.company_id,
    shopifyInitialSync?.dataset_name,
    shopifyInitialSync?.shop,
    shopifyInitialSync?.status
  ]);

  useEffect(() => {
    if (!companyInfo?.company_id || !token) return undefined;

    const hasShopifyContext = !!(
      shopifyInitialSync ||
      companyInfo?.auth_provider === 'shopify' ||
      companyInfo?.platform_type === 'shopify'
    );

    const shouldPoll =
      hasShopifyContext &&
      ['unknown', 'not_started', 'queued', 'running'].includes(backfillState.status || 'unknown');

    console.log(`[backfill-poll] effect_run shouldPoll=${shouldPoll} hasShopifyContext=${hasShopifyContext} status=${backfillState.status} company=${companyInfo?.company_id} hasToken=${!!token}`);
    if (!shouldPoll) return undefined;

    let cancelled = false;

    const checkBackfillStatus = async () => {
      try {
        const params = new URLSearchParams();
        if (companyInfo?.company_id) params.set('company_id', companyInfo.company_id);
        if (companyInfo?.database_name) params.set('dataset_name', companyInfo.database_name);

        const response = await fetchSyncStatusDeduped(
          `${SHOPIFY_AUTH_URL}/auth/shopify/sync-status?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) { console.warn(`[backfill-poll] http_error status=${response.status}`); return; }

        const data = await response.json();
        console.log(`[backfill-poll] response full_backfill_status=${data?.full_backfill_status} orders=${data?.orders_total} customers=${data?.customers_total}`);
        const fullBackfillStatus = data?.full_backfill_status || 'unknown';
        const shouldShowHistoricalSync =
          ['queued', 'running', 'complete'].includes(fullBackfillStatus);

        if (!cancelled) {
          setBackfillState((current) => {
            const nextOrders = Number(data?.orders_total ?? 0);
            const nextCustomers = Number(data?.customers_total ?? 0);
            const nextProducts = Number(data?.products_total ?? 0);
            const nextCollections = Number(data?.collections_total ?? 0);

            return {
              ...current,
              status: fullBackfillStatus,
              hasData: shouldShowHistoricalSync,
              orders: Math.max(Number(current?.orders || 0), nextOrders),
              customers: Math.max(Number(current?.customers || 0), nextCustomers),
              products: Math.max(Number(current?.products || 0), nextProducts),
              collections: Math.max(Number(current?.collections || 0), nextCollections),
            };
          });

          if (
            fullBackfillStatus === 'complete' &&
            backfillStatusRef.current !== 'complete'
          ) {
            setDashboardReloadKey((current) => current + 1);
            routeShopifyUserAfterOnboarding(token, setCurrentView);
          }
          backfillStatusRef.current = fullBackfillStatus;
        }
      } catch (error) {
        console.warn('Backfill status polling failed:', error);
      }
    };

    checkBackfillStatus();
    // FIX (hueco de banners 2026-08-09): antes solo se aceleraba a 5s en
    // queued/running. Justo despues de que el sync inicial termina EN ESTA
    // SESION, el backfill puede seguir en 'unknown'/'not_started' por varios
    // polls (ventana confirmada en logs reales: ~40s con el poll viejo de
    // 15s) -> ningun banner visible en el medio. Mientras dure ese handoff
    // (acotado a 90s via shopifySyncCompletedAtRef, ver render mas abajo),
    // pollear tambien a 5s en unknown/not_started para cerrar el hueco rapido.
    const inHandoffWindow =
      shopifySyncJustCompletedRef.current &&
      shopifySyncCompletedAtRef.current &&
      (Date.now() - shopifySyncCompletedAtRef.current) < 90000;
    const pollMs = (['queued', 'running'].includes(backfillState.status) || inHandoffWindow) ? 5000 : 15000;
    const intervalId = window.setInterval(checkBackfillStatus, pollMs);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    companyInfo?.company_id,
    companyInfo?.auth_provider,
    companyInfo?.platform_type,
    token,
    shopifyInitialSync,
    backfillState.status,
    backfillState.hasData
]);

  const updateShopifyInitialSync = (updates) => {
    setShopifyInitialSync((current) => {
      const next = { ...(current || {}), ...updates, updated_at: Date.now() };
      localStorage.setItem('shopifyInitialSync', JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    if (!shopifyInitialSync || shopifyInitialSync.status === 'complete' || shopifyInitialSync.status === 'dismissed') {
      return undefined;
    }
    const syncCompanyId = shopifyInitialSync.company_id;
    const syncDatasetName = shopifyInitialSync.dataset_name;
    const syncShop = shopifyInitialSync.shop;
    const renderKey = `${syncCompanyId || ''}:${syncDatasetName || ''}:${shopifyInitialSync.status || ''}`;
    if (shopifySetupRenderedRef.current !== renderKey) {
      shopifySetupRenderedRef.current = renderKey;
      logShopifyEntryUi({
        request_id: getShopifyEntryUiRequestId(),
        shop: syncShop,
        company_id: syncCompanyId,
        dataset_name: syncDatasetName,
        stage: 'setup_screen_rendered',
        status: shopifyInitialSync.status || 'unknown',
      });
    }

    const interval = window.setInterval(() => {
      setShopifyProgressIndex((current) => (current + 1) % shopifyProgressMessages.length);
    }, 3000);

    return () => window.clearInterval(interval);
  }, [shopifyInitialSync]);

  useEffect(() => {
    if (!token || !shopifyInitialSync || shopifyInitialSync.status === 'complete' || shopifyInitialSync.status === 'dismissed') {
      return undefined;
    }

    let cancelled = false;
    const syncCompanyId = shopifyInitialSync.company_id;
    const syncDatasetName = shopifyInitialSync.dataset_name;
    const syncShop = shopifyInitialSync.shop;
    const syncKey = `${syncCompanyId || ''}:${syncDatasetName || ''}`;

    const syncStatusUrl = () => {
      const params = new URLSearchParams();
      if (syncCompanyId) params.set('company_id', syncCompanyId);
      if (syncDatasetName) params.set('dataset_name', syncDatasetName);
      if (syncShop) params.set('shop', syncShop);
      const query = params.toString();
      return `${SHOPIFY_AUTH_URL}/auth/shopify/sync-status${query ? `?${query}` : ''}`;
    };

    const pollStatus = async () => {
      const latest = shopifySyncStateRef.current;
      if (cancelled || latest?.status === 'complete' || latest?.status === 'dismissed') return;

      try {
        const pollStart = performance.now();
        logShopifyEntryUi({
          request_id: getShopifyEntryUiRequestId(),
          shop: syncShop,
          company_id: syncCompanyId,
          dataset_name: syncDatasetName,
          stage: 'sync_status_poll_start',
          status: 'started',
        });
        const response = await fetchSyncStatusDeduped(syncStatusUrl(), {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        logShopifyEntryUi({
          request_id: getShopifyEntryUiRequestId(),
          shop: syncShop,
          company_id: syncCompanyId,
          dataset_name: syncDatasetName,
          stage: 'sync_status_poll_response',
          status: response.status,
          duration_ms: shopifyEntryUiDurationMs(pollStart),
          ready: data.ready === true,
          orders: data.orders,
          customers: data.customers,
          products: data.products,
        });

        if (response.ok) {
          updateShopifyInitialSync({
            orders: data.orders,
            customers: data.customers,
            products: data.products,
            ready: data.ready,
            lastStatus: data.status,
          });
        }

        if (response.ok && hasCompletedShopifySync(data)) {
          const latest = shopifySyncStateRef.current;
          if (latest?.status !== 'complete' && latest?.status !== 'dismissed') {
            logShopifyEntryUi({
              request_id: getShopifyEntryUiRequestId(),
              shop: syncShop,
              company_id: syncCompanyId,
              dataset_name: syncDatasetName,
              stage: 'shopify_initial_sync_marked_complete',
              status: 'poll_status',
            });
            shopifySyncJustCompletedRef.current = true;
            shopifySyncCompletedAtRef.current = Date.now();
            updateShopifyInitialSync({ status: 'complete', result: data });
            logShopifyEntryUi({
              request_id: getShopifyEntryUiRequestId(),
              shop: syncShop,
              company_id: syncCompanyId,
              dataset_name: syncDatasetName,
              stage: 'dashboard_reload_triggered',
              status: 'poll_status',
            });
            setDashboardReloadKey((current) => current + 1);
          }
        }
      } catch (error) {
        console.warn('Shopify sync status polling failed:', error);
        logShopifyEntryUi({
          request_id: getShopifyEntryUiRequestId(),
          shop: syncShop,
          company_id: syncCompanyId,
          dataset_name: syncDatasetName,
          stage: 'sync_status_poll_error',
          status: 'error',
          error_type: error?.name || 'Error',
          error_message: error?.message || 'unknown',
        });
      }
    };

    const runInitialSync = async () => {
      if (shopifySyncStartedRef.current === syncKey) return;
      shopifySyncStartedRef.current = syncKey;
      updateShopifyInitialSync({ status: 'running' });

      try {
        const syncStart = performance.now();
        logShopifyEntryUi({
          request_id: getShopifyEntryUiRequestId(),
          shop: syncShop,
          company_id: syncCompanyId,
          dataset_name: syncDatasetName,
          stage: 'run_initial_sync_request_start',
          status: 'started',
        });
        const response = await fetch(`${SHOPIFY_AUTH_URL}/auth/shopify/run-initial-sync`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            company_id: syncCompanyId,
            dataset_name: syncDatasetName,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        logShopifyEntryUi({
          request_id: getShopifyEntryUiRequestId(),
          shop: syncShop,
          company_id: syncCompanyId,
          dataset_name: syncDatasetName,
          stage: response.ok ? 'run_initial_sync_response_success' : 'run_initial_sync_response_error',
          status: response.status,
          duration_ms: shopifyEntryUiDurationMs(syncStart),
          orders: data.orders,
          customers: data.customers,
          products: data.products,
        });

        if (response.ok && hasCompletedShopifySync(data)) {
          logShopifyEntryUi({
            request_id: getShopifyEntryUiRequestId(),
            shop: syncShop,
            company_id: syncCompanyId,
            dataset_name: syncDatasetName,
            stage: 'shopify_initial_sync_marked_complete',
            status: 'run_initial_sync',
          });
          shopifySyncJustCompletedRef.current = true;
          shopifySyncCompletedAtRef.current = Date.now();
          updateShopifyInitialSync({ status: 'complete', result: data });
          logShopifyEntryUi({
            request_id: getShopifyEntryUiRequestId(),
            shop: syncShop,
            company_id: syncCompanyId,
            dataset_name: syncDatasetName,
            stage: 'dashboard_reload_triggered',
            status: 'run_initial_sync',
          });
          setDashboardReloadKey((current) => current + 1);
        } else {
          updateShopifyInitialSync({ status: 'pending' });
        }
      } catch (error) {
        console.warn('Shopify initial sync is still running:', error);
        logShopifyEntryUi({
          request_id: getShopifyEntryUiRequestId(),
          shop: syncShop,
          company_id: syncCompanyId,
          dataset_name: syncDatasetName,
          stage: 'run_initial_sync_response_error',
          status: 'error',
          error_type: error?.name || 'Error',
          error_message: error?.message || 'unknown',
        });
        if (!cancelled) {
          updateShopifyInitialSync({ status: 'pending' });
        }
      }
    };

    runInitialSync();
    pollStatus();
    const pollInterval = window.setInterval(pollStatus, 12000);

    return () => {
      cancelled = true;
      window.clearInterval(pollInterval);
    };
  }, [token, shopifyInitialSync?.company_id, shopifyInitialSync?.dataset_name, shopifyInitialSync?.shop]);

  useEffect(() => {
    if (token && currentView === 'dashboard') {
      logShopifyEntryUi({
        request_id: getShopifyEntryUiRequestId(),
        shop: localStorage.getItem('shopifyShop') || '',
        company_id: localStorage.getItem('companyId') || localStorage.getItem('shopifyCompanyId') || '',
        dataset_name: localStorage.getItem('datasetId') || '',
        stage: 'dashboard_render_with_reload_key',
        status: 'rendered',
        reload_key: dashboardReloadKey,
      });
    }
  }, [token, currentView, dashboardReloadKey]);

  // Escuchar evento de toggle sidebar desde Dashboard
  useEffect(() => {
    const handleSidebarToggle = (event) => {
      if (event.detail && typeof event.detail.collapsed !== 'undefined') {
        setShowSidebar(!event.detail.collapsed);
      }
    };

    window.addEventListener('toggleSidebar', handleSidebarToggle);
    return () => window.removeEventListener('toggleSidebar', handleSidebarToggle);
  }, []);

  // Aplicar clase de tema al document.documentElement
  useEffect(() => {
    document.documentElement.className = darkMode ? 'dark-mode' : 'light-mode';
  }, [darkMode]);

  // Función auxiliar para limpiar bloques de código JSON/JS del texto
  // Definida fuera para ser usada en loadConversations y handleSendMessage
  const cleanTextFromChartJson = (content) => {
    if (!content) return '';
    // Regex más robusta para encontrar bloques de código que parecen JSON de visualización
    return content.replace(/```(?:json|js|)?\s*(\{[\s\S]*?"type"\s*:\s*"visualization"[\s\S]*?\})\s*```/g, '')
                  .replace(/```(?:json|js|)?\s*(\{[\s\S]*?"visualizationDetails"[\s\S]*?\})\s*```/g, '')
                  .trim();
  };

  // Cargar conversaciones desde el backend
  const loadConversations = async () => {
    if (!token) return;
    
    try {
      setConversationsLoading(true);
      // Extraer user_id del token
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.sub || payload.user_id || payload.username || payload.user;
      
      if (!userId) {
        console.error('No se pudo extraer user_id del token');
        return;
      }
      
      const response = await fetch(`${API_URL}/conversations?user_id=${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      } else {
        console.error('Error al cargar conversaciones:', response.statusText);
      }
    } catch (error) {
      console.error('Error al cargar conversaciones:', error);
    } finally {
      setConversationsLoading(false);
    }
  };
  
  // Cargar conversaciones cuando el usuario se autentica
  useEffect(() => {
    if (token) {
      loadConversations();

      // Recuperar companyInfo si se perdió en reload
      if (!companyInfo) {
        // Reconstruye un companyInfo provisorio SOLO con datos que ya viven en
        // el navegador (localStorage + claims del JWT). No hace red. Devuelve
        // null si no hay datos suficientes para pintar la pantalla.
        const buildFallbackCompanyInfo = () => {
          try {
            const { p, d } = decodeJwtClaims(token);
            const storedDataset = localStorage.getItem('datasetId') || d;
            const storedProject = localStorage.getItem('projectId') || p || 'primeval-gear-417120';

            // companyInfo completo persistido de un /protected previo (más rico).
            let persisted = null;
            try {
              const raw = localStorage.getItem('companyInfo');
              if (raw) persisted = JSON.parse(raw);
            } catch (_) { persisted = null; }

            const payload = JSON.parse(atob(token.split('.')[1]));
            // FIX (colada de empresa 2026-08-09): antes esta funcion priorizaba
            // el `companyInfo` persistido (blob completo de un /protected previo,
            // puede ser de OTRA tienda/empresa si el navegador tenia sesion vieja)
            // por encima de las señales FRESCAS que ShopifyComplete.jsx escribe
            // sincronicamente en localStorage/JWT durante el exchange de la
            // tienda que se esta conectando AHORA. Resultado real observado en
            // logs: al entrar con coffeetest-5276 (COFF5276), el fallback pintaba
            // "company=LOVE" (empresa vieja) hasta que /protected corregia unos
            // segundos despues -> unos segundos de fetches/lecturas con la
            // empresa equivocada. Ahora la señal fresca (JWT claim o
            // companyId/shopifyCompanyId recien escritos) manda; `persisted`
            // solo se usa para ENRIQUECER cuando coincide con esa empresa fresca.
            const freshCompanyId =
              payload.company_id
              || localStorage.getItem('companyId')
              || localStorage.getItem('shopifyCompanyId');
            const companyId = freshCompanyId || (persisted && persisted.company_id);
            const dataset = storedDataset || (persisted && persisted.database_name);

            if (!companyId || !dataset) return null;

            const fallbackCompanyInfo = persisted && persisted.company_id === companyId
              ? { ...persisted, project_id: persisted.project_id || storedProject, database_name: dataset }
              : {
                  project_id: storedProject,
                  company_id: companyId,
                  company_name: payload.company_name || payload.CompanyName || companyId,
                  database_name: dataset,
                  status: 'Active'
                };
            return { fallbackCompanyInfo, storedProject, dataset };
          } catch (e) {
            return null;
          }
        };

        // Aplica los datos reales de /protected (fuente de verdad) y los
        // persiste para que el próximo arranque tenga un fallback más rico.
        const applyProtected = (data) => {
          const restoredProjectId = data.company_info.project_id || 'primeval-gear-417120';
          const restoredDatasetId = data.company_info.database_name;
          const restoredCompanyId = data.company_info.company_id;
          const restoredLanguage = data.language || data.company_info.language || 'en';

          setCompanyInfo(data.company_info);
          setProjectId(restoredProjectId);
          if (restoredDatasetId) setDatasetId(restoredDatasetId);
          if (restoredCompanyId) localStorage.setItem('companyId', restoredCompanyId);
          if (restoredDatasetId) localStorage.setItem('datasetId', restoredDatasetId);
          localStorage.setItem('projectId', restoredProjectId);
          localStorage.setItem('language', restoredLanguage);
          // Persistimos el companyInfo completo (datos de la propia empresa del
          // usuario logueado: id/nombre/dataset/proyecto/status; sin tokens ni
          // datos sensibles) para enriquecer el fallback de arranque.
          try { localStorage.setItem('companyInfo', JSON.stringify(data.company_info)); } catch (_) {}
        };

        // Backoff CORTO: /protected es un simple chequeo HTTP; no tiene sentido
        // esperar 5/10/15/20/25s como antes (el usuario veía ~20s de demora).
        // Reintentos ampliados para el ONBOARDING (fix 2026-07-31): al instalar
        // una tienda NUEVA la empresa se acaba de INSERTar en BigQuery y hay una
        // race de consistencia read-after-write -> /protected devuelve 400 unas
        // veces y 200 otras durante los primeros segundos hasta que BQ propaga
        // la fila de MetaData.Companies. Con solo 3 intentos (1s/2s) el frontend
        // se rendia y quedaba con el fallback local sin la info real. Ahora se
        // hacen hasta 8 intentos con backoff 1s,2s,2s,3s,3s,3s,2s ~= 16s totales,
        // que ABSORBEN esa ventana de propagacion y caben DENTRO del watchdog de
        // 20s (ver mas abajo): o /protected resuelve con datos reales y pinta,
        // o el watchdog cae a dashboard con el fallback. El 400/5xx/red se trata
        // como TRANSITORIO solo en este arranque; el 401/403 NUNCA reintenta
        // (sesion muerta -> logout inmediato).
        //
        // Backoff por intento fallido (indice = attempt): [1]=1s [2]=2s [3]=2s
        // [4]=3s [5]=3s [6]=3s [7]=2s. Total acotado (~16s), nunca infinito.
        const MAX_PROTECTED_ATTEMPTS = 8;
        const PROTECTED_BACKOFF_MS = [0, 1000, 2000, 2000, 3000, 3000, 3000, 2000];
        const protectedWaitMs = (attempt) =>
          PROTECTED_BACKOFF_MS[attempt] ?? 2000;
        //
        // La función distingue TRES resultados y los devuelve con un sobre
        // { status, data } para que el llamador actúe distinto (fix hallazgo
        // del revisor 2026-07-27):
        //   - { status: 'ok', data }         -> sesión válida, datos reales.
        //   - { status: 'invalid_session' }  -> el servidor respondió 401/403
        //        EXPLÍCITO: la sesión está muerta/revocada/expirada. SIEMPRE
        //        hay que desloguear y volver al login, haya o no fallback.
        //   - { status: 'no_response' }      -> red caída / error / se
        //        agotaron los reintentos sin confirmar nada. NO desloguear
        //        (sería peor echar gente por lentitud del servidor); se
        //        mantiene el fallback pintado si lo había.
        const fetchProtectedWithRetry = async (attempt = 1) => {
          let r;
          try {
            r = await fetch(`${AUTH_URL}/protected`, {
              headers: { "Authorization": `Bearer ${token}` }
            });
          } catch (netErr) {
            // Error de red (fetch rechaza). Reintentar como con un 5xx.
            if (attempt < MAX_PROTECTED_ATTEMPTS) {
              const waitMs = protectedWaitMs(attempt);
              console.warn(`[protected-retry] attempt=${attempt}/${MAX_PROTECTED_ATTEMPTS} error de red, reintentando en ${waitMs / 1000}s`, netErr);
              await new Promise(res => setTimeout(res, waitMs));
              return fetchProtectedWithRetry(attempt + 1);
            }
            console.warn(`[protected-retry] agotados los ${MAX_PROTECTED_ATTEMPTS} intentos por error de red`, netErr);
            return { status: 'no_response' };
          }
          if (r.ok) {
            const data = await r.json();
            if (attempt > 1) {
              console.log(`[protected-retry] resuelto OK en el intento ${attempt}/${MAX_PROTECTED_ATTEMPTS} (propagación BigQuery absorbida)`);
            }
            return { status: 'ok', data };
          }
          if (r.status === 401 || r.status === 403) {
            // Sesión inválida CONFIRMADA por el servidor: no reintentar NUNCA,
            // ni siquiera en el arranque. 401/403 no es la race de BigQuery; es
            // sesión muerta/revocada/expirada -> logout inmediato.
            console.warn(`[protected-retry] sesión inválida status=${r.status}, no se reintenta`);
            return { status: 'invalid_session' };
          }
          // 400 (race read-after-write de BigQuery en onboarding) y 5xx se
          // tratan como TRANSITORIOS: reintentar hasta agotar la ventana.
          if (attempt < MAX_PROTECTED_ATTEMPTS) {
            const waitMs = protectedWaitMs(attempt);
            console.warn(`[protected-retry] attempt=${attempt}/${MAX_PROTECTED_ATTEMPTS} status=${r.status} (transitorio, p.ej. propagación BigQuery), reintentando en ${waitMs / 1000}s`);
            await new Promise(res => setTimeout(res, waitMs));
            return fetchProtectedWithRetry(attempt + 1);
          }
          console.warn(`[protected-retry] agotados los ${MAX_PROTECTED_ATTEMPTS} intentos, status=${r.status} -> se mantiene el fallback local`);
          return { status: 'no_response' };
        };

        // Limpieza total de sesión, idéntica a la del logout manual del sidebar
        // (token + storage + estado React). Se usa cuando /protected confirma
        // 401/403: aunque hayamos pintado un fallback, la sesión está muerta y
        // hay que devolver al usuario al login en vez de dejarlo navegando.
        const forceLogoutInvalidSession = () => {
          console.warn('[protected-retry] sesión inválida confirmada -> logout forzado');
          localStorage.removeItem('token');
          clearStoredSessionContext();
          setToken(null);
          resetAuthenticatedContextState();
          setCurrentView(null);
        };

        // 1) Pintar YA con el fallback local si alcanza (sin esperar red).
        const fb = buildFallbackCompanyInfo();
        const hasFastFallback = !!fb;
        if (hasFastFallback) {
          setCompanyInfo(fb.fallbackCompanyInfo);
          setProjectId(fb.storedProject);
          setDatasetId(fb.dataset);
          if (!billingReturnRef.current) {
            routeShopifyUserAfterOnboarding(token, setCurrentView);
          }
        }

        // 2) En paralelo, confirmar/actualizar con /protected (fuente de verdad).
        //    Si ya pintamos con fallback, esto corre en segundo plano y solo
        //    actualiza cuando llegan los datos reales.
        fetchProtectedWithRetry()
          .then(result => {
            // 401/403 confirmado: la sesión está muerta. Desloguear SIEMPRE,
            // haya fallback pintado o no (fix hallazgo del revisor 2026-07-27:
            // antes, con fallback, un 401/403 quedaba silencioso y el usuario
            // seguía navegando con una sesión inválida — p.ej. tras una
            // rotación de JWT_SECRET_KEY en producción).
            if (result?.status === 'invalid_session') {
              forceLogoutInvalidSession();
              return;
            }
            if (result?.status === 'ok' && result.data?.company_info) {
              applyProtected(result.data);
              console.log("companyInfo restored:", result.data.company_info.company_id);
              if (!hasFastFallback && !billingReturnRef.current && !adminReturnRef.current) {
                routeShopifyUserAfterOnboarding(token, setCurrentView);
              }
            } else if (!hasFastFallback) {
              // 'no_response' (o datos vacíos) SIN fallback local: no dejar el
              // routing sin correr (fix incidente 2026-07-18). Con fallback
              // pintado no tocamos nada: el usuario sigue viendo su dashboard
              // local mientras el servidor se recupera.
              setCompanyInfo(null);
              routeShopifyUserAfterOnboarding(token, setCurrentView);
            }
          })
          .catch(err => {
            // Error inesperado en el .then (no de red del fetch, ya manejado):
            // tratarlo como 'no_response', nunca como sesión inválida.
            console.error("Error restoring companyInfo:", err);
            if (!hasFastFallback) {
              setCompanyInfo(null);
              routeShopifyUserAfterOnboarding(token, setCurrentView);
            }
          });
      }
      
      // Restaurar conversación actual si existe
      const savedSessionId = localStorage.getItem('sessionId');
      const savedConversationId = localStorage.getItem('currentConversationId');
      
      if (savedSessionId && savedConversationId) {
        console.log(`[RESTORE] Restaurando conversación: ${savedConversationId}`);
        // Esperar un momento para que las conversaciones se carguen
        setTimeout(() => {
          switchConversation(savedConversationId);
        }, 500);
      }
    }
  }, [token]);

  // WATCHDOG (fix incidente 2026-07-18): la salida del estado "Preparing your
  // workspace..." depende de una cadena de fetches sin timeout
  // (/protected con reintentos -> sync-status -> health-score + summary). Si
  // cualquiera cuelga o falla de forma no prevista, currentView queda en null
  // para siempre. Tras 20s con sesion activa y sin vista, caer a Dashboard;
  // si el routing real resuelve despues, ajusta la vista igual.
  useEffect(() => {
    if (!token || currentView !== null) return undefined;
    const id = window.setTimeout(() => {
      setCurrentView((view) => (view === null ? 'dashboard' : view));
    }, 20000);
    return () => window.clearTimeout(id);
  }, [token, currentView]);

  // Sidebar muestra el NOMBRE real de la tienda, no el codigo (fix 2026-07-19):
  // el token//protected a veces solo trae el CompanyId (p.ej. TUCK1), asi que
  // company_name cae al codigo. /revenue-finder/summary devuelve el
  // CompanyName real de MetaData.Companies; si el nombre actual es el codigo
  // (o falta), lo resolvemos desde ahi una sola vez.
  useEffect(() => {
    const companyId = companyInfo?.company_id || companyInfo?.CompanyId;
    const currentName = companyInfo?.company_name;
    if (!token || !companyId) return undefined;
    if (currentName && currentName !== companyId) return undefined;
    let cancelled = false;
    // dedupFetch: reusa la respuesta del /revenue-finder/summary ya pedido por
    // el routing de onboarding (o por RevenueCheckpoint/RevenueFinder al
    // montar) en vez de disparar un fetch nuevo solo para leer company_name.
    dedupFetch(`${DASHBOARD_API_URL}/revenue-finder/summary?company=${encodeURIComponent(companyId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const realName = data?.company_name;
        if (!cancelled && realName && realName !== companyId) {
          setCompanyInfo((prev) => (prev ? { ...prev, company_name: realName } : prev));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, companyInfo]);

  useEffect(() => {
    if (!token || !companyInfo || currentView !== null) return;
    // Deep links (bookmarks) must respect the readiness gate: only apply the
    // URL-requested view if the routing decided the account is ready (i.e. it
    // chose 'checkpoint'). Accounts mid-sync land on Dashboard regardless.
    routeShopifyUserAfterOnboarding(token, (routedView) => {
      const requestedView = deepLinkViewRef.current;
      deepLinkViewRef.current = null;
      if (routedView === 'checkpoint' && requestedView) {
        setCurrentView(requestedView);
      } else {
        setCurrentView(routedView);
      }
    });
  }, [token, companyInfo, currentView]);

  // Persistir sessionId y currentConversationId en localStorage
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('sessionId', sessionId);
    }
    if (currentConversationId) {
      localStorage.setItem('currentConversationId', currentConversationId);
    }
  }, [sessionId, currentConversationId]);
  
  // Actualizar cache cuando cambie el chatHistory
  useEffect(() => {
    if (currentConversationId && chatHistory.length > 0) {
      setConversationCache(prev => ({
        ...prev,
        [currentConversationId]: chatHistory
      }));
    }
  }, [chatHistory, currentConversationId]);

  // Efecto para hacer scroll hacia el último mensaje
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Auto-resize del textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [message]);

  // Decodificar JWT para intentar precargar project/dataset si vienen como claims
  const decodeJwtClaims = (jwt) => {
    try {
      const base64Url = jwt.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      const payload = JSON.parse(jsonPayload);
      // Intentar varias claves
      const p = payload.project_id || payload.projectId || payload.project || '';
      const d = payload.dataset_id || payload.datasetId || payload.dataset || '';
      return { p, d };
    } catch (e) {
      return { p: '', d: '' };
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      clearStoredSessionContext();
      resetAuthenticatedContextState();
      const response = await fetch(`${AUTH_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (response.ok) {
        const newToken = data.access_token; // Asumiendo que el token viene en 'access_token'
        setToken(newToken);
        localStorage.setItem('token', newToken); // Guardar token en localStorage
        // Resetear sesión y chat al cambiar de usuario
        
        // Llamar a /protected para obtener project_id y dataset_id
        try {
          const protectedResponse = await fetch(`${AUTH_URL}/protected`, {
            method: 'GET',
            headers: { 
              'Authorization': `Bearer ${newToken}`
            }
          });
          
          if (protectedResponse.ok) {
            const protectedData = await protectedResponse.json();
            console.log('Protected data:', protectedData);
            
            // Extraer project_id y database_name (dataset_id)
            if (protectedData.company_info) {
              const projectId = protectedData.company_info.project_id || 'primeval-gear-417120';
              const datasetId = protectedData.company_info.database_name;
              const companyId = protectedData.company_info.company_id;
              const sessionLanguage = protectedData.language || protectedData.company_info.language || 'en';
              
              setProjectId(projectId);
              localStorage.setItem('projectId', projectId);
              if (datasetId) {
                setDatasetId(datasetId);
                localStorage.setItem('datasetId', datasetId);
              }
              localStorage.setItem('user', username);
              if (companyId) localStorage.setItem('companyId', companyId);
              localStorage.setItem('language', sessionLanguage);
              setCompanyInfo(protectedData.company_info);
              // Persistir companyInfo completo para el fallback de arranque.
              try { localStorage.setItem('companyInfo', JSON.stringify(protectedData.company_info)); } catch (_) {}

              console.log('Auto-configured:', { projectId, datasetId, company: protectedData.company_info.company_name });
              
              // Crear sesión en el backend del chat (no usar session_id de /protected)
              try {
                if (!projectId || !datasetId) {
                  console.error('Cannot create chat session without projectId and datasetId', { projectId, datasetId });
                } else {
                  const chatSessionResponse = await fetch(`${API_URL}/create-session`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${newToken}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      project_id: projectId,
                      dataset_id: datasetId,
                      user_id: protectedData?.user_id || protectedData?.user?.id || protectedData?.company_info?.user_id || protectedData?.company_info?.company_id || 'shopify-user',
                      language,
                    }),
                  });
                  
                  if (chatSessionResponse.ok) {
                    const chatSessionData = await chatSessionResponse.json();
                    setSessionId(chatSessionData.session_id);
                    console.log('Chat session created:', chatSessionData.session_id);
                    
                    // La conversación se creará automáticamente en BigQuery cuando se envíe el primer mensaje
                    setCurrentConversationId(chatSessionData.session_id);
                  } else {
                    console.error('Failed to create chat session');
                  }
                }
              } catch (chatSessionError) {
                console.error('Error creating chat session:', chatSessionError);
              }
            }
          } else {
            console.warn('Failed to fetch protected data, falling back to JWT claims');
            setCompanyInfo(null);
            // Fallback: Intentar precargar project/dataset desde claims del JWT
            const { p, d } = decodeJwtClaims(newToken);
            if (p) setProjectId(p);
            if (d) setDatasetId(d);
          }
        } catch (protectedError) {
          console.error('Error fetching protected data:', protectedError);
          setCompanyInfo(null);
          // Fallback: Intentar precargar project/dataset desde claims del JWT
          const { p, d } = decodeJwtClaims(newToken);
          if (p) setProjectId(p);
          if (d) setDatasetId(d);
        }
      } else {
        throw new Error(data.detail || 'Failed to login');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert(`${language === 'en' ? 'Login error' : 'Error de login'}: ${error.message}`);
    }
    setIsLoading(false);
  };

  const handleNewSession = async () => {
    if (!projectId || !datasetId) {
      console.error('Cannot create chat session without projectId and datasetId', { projectId, datasetId });
      alert(language === 'en' ? 'You must specify project_id and dataset_id before sending messages.' : 'Debes especificar project_id y dataset_id antes de enviar mensajes.');
      return;
    }

    setIsLoading(true);
    setChatHistory([]);
    setMetrics(null); // Resetear métricas
    
    // Limpiar localStorage
    localStorage.removeItem('sessionId');
    localStorage.removeItem('currentConversationId');
    
    // Crear una nueva sesión de chat
    try {
      const response = await fetch(`${API_URL}/create-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_id: projectId,
          dataset_id: datasetId,
          user_id: companyInfo?.user_id || companyInfo?.company_id || 'shopify-user',
          language,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setSessionId(data.session_id);
        setCurrentConversationId(data.session_id);
        localStorage.setItem('sessionId', data.session_id);
        localStorage.setItem('currentConversationId', data.session_id);
        console.log('New chat session created:', data.session_id);
        
        // La conversación se creará en BigQuery automáticamente cuando se envíe el primer mensaje
        // No necesitamos crear la conversación localmente
      } else {
        throw new Error(data.detail || 'Failed to create session');
      }
    } catch (error) {
      console.error('Error creating session:', error);
      alert(`${language === 'en' ? 'Error' : 'Error'}: ${error.message}`);
    }
    setIsLoading(false);
  };

  // Cambiar a una conversación existente
  const switchConversation = async (conversationId) => {
    try {
      setIsLoading(true);
      
      // Verificar si ya está en cache
      if (conversationCache[conversationId]) {
        console.log('[CACHE] Usando conversación cacheada:', conversationId);
        setCurrentConversationId(conversationId);
        setSessionId(conversationId);
        setChatHistory(conversationCache[conversationId]);
        setMetrics(null);
        setIsLoading(false);
        return;
      }
      
      // Si no está en cache, cargar desde el backend
      console.log('[CACHE] Cargando conversación desde backend:', conversationId);
      const response = await fetch(`${API_URL}/session/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`[LOAD] Conversación cargada desde: ${data.source.toUpperCase()} - ${data.messages.length} mensajes`);
        setCurrentConversationId(conversationId);
        setSessionId(conversationId);
        
        // Convertir mensajes del backend al formato del frontend
        const formattedMessages = [];
        data.messages.forEach((msg) => {
          if (msg.role === 'user') {
            formattedMessages.push({
              sender: 'user',
              text: msg.content
            });
          } else if (msg.role === 'assistant') {
            // Primero verificar si viene chart_data separado desde BigQuery
            if (msg.chart_data) {
              try {
                // chart_data puede venir como objeto (ya parseado) o como string
                const chartData = typeof msg.chart_data === 'string' 
                  ? JSON.parse(msg.chart_data) 
                  : msg.chart_data;
                
                console.log('[LOAD] chart_data encontrado:', chartData);
                
                // Agregar texto sin el bloque de código
                const textWithoutChart = cleanTextFromChartJson(msg.content);
                
                if (textWithoutChart) {
                  formattedMessages.push({
                    sender: 'bot',
                    text: textWithoutChart
                  });
                }
                // Agregar gráfico
                formattedMessages.push({
                  sender: 'bot',
                  viz: chartData
                });
              } catch (e) {
                console.error('Error al parsear chart_data:', e, msg.chart_data);
                formattedMessages.push({
                  sender: 'bot',
                  text: msg.content
                });
              }
            } else {
              // Si no viene chart_data separado, intentar extraer del contenido
              // Buscamos bloques de código que parezcan JSON
              const codeBlockRegex = /```(?:json|js|)?\s*(\{[\s\S]*?\})\s*```/g;
              let match;
              let foundViz = false;
              let vizData = null;
              
              // Iterar sobre todos los bloques de código para encontrar uno que sea visualización
              while ((match = codeBlockRegex.exec(msg.content)) !== null) {
                try {
                  const potentialJson = JSON.parse(match[1]);
                  if (potentialJson.type === 'visualization' || potentialJson.visualizationDetails) {
                    vizData = potentialJson;
                    foundViz = true;
                    break; // Encontramos la visualización, paramos de buscar
                  }
                } catch (e) {
                  // No es JSON válido, continuar
                }
              }

              if (foundViz && vizData) {
                // Separar texto y gráfico
                const textWithoutChart = cleanTextFromChartJson(msg.content);
                
                if (textWithoutChart) {
                  formattedMessages.push({
                    sender: 'bot',
                    text: textWithoutChart
                  });
                }
                formattedMessages.push({
                  sender: 'bot',
                  viz: vizData
                });
              } else {
                formattedMessages.push({
                  sender: 'bot',
                  text: msg.content
                });
              }
            }
          }
        });
        
        // Guardar en cache
        setConversationCache(prev => ({
          ...prev,
          [conversationId]: formattedMessages
        }));
        
        setChatHistory(formattedMessages);
        setMetrics(null);
      } else {
        console.error('Error al cargar mensajes:', response.statusText);
        alert(language === 'en' ? 'Error loading conversation' : 'Error al cargar la conversación');
      }
    } catch (error) {
      console.error('Error al cambiar de conversación:', error);
      alert(language === 'en' ? 'Error switching conversation' : 'Error al cambiar de conversación');
    } finally {
      setIsLoading(false);
    }
  };

  // Eliminar una conversación
  const deleteConversation = async (conversationId) => {
    if (window.confirm(language === 'en' ? 'Are you sure you want to delete this conversation?' : '¿Estás seguro de eliminar esta conversación?')) {
      try {
        const response = await fetch(`${API_URL}/conversations/${conversationId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          // Eliminar de la lista local
          setConversations(prev => prev.filter(c => c.session_id !== conversationId));
          
          if (currentConversationId === conversationId) {
            setChatHistory([]);
            setSessionId(null);
            setCurrentConversationId(null);
          }
        } else {
          console.error('Error al eliminar conversación:', response.statusText);
          alert(language === 'en' ? 'Error deleting conversation' : 'Error al eliminar la conversación');
        }
      } catch (error) {
        console.error('Error al eliminar conversación:', error);
        alert(language === 'en' ? 'Error deleting conversation' : 'Error al eliminar la conversación');
      }
    }
  };

  // El backend ahora maneja automáticamente la actualización del título de conversación
  // basado en el primer mensaje del usuario, por lo que no necesitamos este useEffect
  
  // Recargar conversaciones después de enviar un mensaje para reflejar cambios en títulos
  // Solo hacerlo cuando hay pocos mensajes (inicio de conversación) para evitar spam
  useEffect(() => {
    if (currentConversationId && chatHistory.length > 0 && chatHistory.length <= 4) {
      // Recargar conversaciones para obtener el título actualizado del backend
      loadConversations();
    }
  }, [chatHistory.length, currentConversationId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation(); // Evitar propagación
      handleSendMessage(e);
    }
  };

  // Función para copiar mensaje
  const handleCopyMessage = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageIndex(index);
      setTimeout(() => setCopiedMessageIndex(null), 2000); // Mostrar "Copiado" por 2 segundos
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };
  
  const toggleQueryExpansion = (messageIndex) => {
    setExpandedQueries(prev => ({
      ...prev,
      [messageIndex]: !prev[messageIndex]
    }));
  };
  
  const handleCopyQuery = async (query) => {
    try {
      await navigator.clipboard.writeText(query);
      console.log('✅ Query copiada al portapapeles');
    } catch (err) {
      console.error('Error al copiar query:', err);
    }
  };

  // Función para dar feedback positivo
  const handlePositiveFeedback = async (index) => {
    // Prevenir doble envío
    if (feedbackSubmitting) return;
    
    const isCurrentlyPositive = messageFeedback[index] === 'positive';
    
    setMessageFeedback(prev => ({
      ...prev,
      [index]: isCurrentlyPositive ? null : 'positive' // Toggle
    }));
    
    // Si se está marcando como positivo (no desmarcando), guardar en backend
    if (!isCurrentlyPositive) {
      setFeedbackSubmitting(true);
      try {
        const response = await fetch(`${API_URL}/feedback/${sessionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            message_index: index,
            feedback_type: 'positive',
            issues: [],
            comment: null
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Feedback positivo guardado: ${data.feedback_id}`);
        } else {
          const errorText = await response.text();
          console.error('❌ Error al guardar feedback positivo:', response.status, errorText);
        }
      } catch (err) {
        console.error('❌ Error de red al guardar feedback positivo:', err);
      } finally {
        setFeedbackSubmitting(false);
      }
    }
  };

  // Función para dar feedback negativo - abre modal
  const handleNegativeFeedback = (index) => {
    if (messageFeedback[index] === 'negative') {
      // Si ya está marcado como negativo, desmarcarlo
      setMessageFeedback(prev => ({
        ...prev,
        [index]: null
      }));
    } else {
      // Abrir modal para feedback detallado
      setFeedbackModalData({
        messageIndex: index,
        feedbackText: '',
        selectedIssues: []
      });
      setShowFeedbackModal(true);
    }
  };

  // Función para enviar feedback negativo con detalles
  const handleSubmitNegativeFeedback = async () => {
    // Prevenir doble envío
    if (feedbackSubmitting) return;
    
    const { messageIndex, feedbackText, selectedIssues } = feedbackModalData;
    
    setMessageFeedback(prev => ({
      ...prev,
      [messageIndex]: 'negative'
    }));
    
    // Guardar feedback en backend
    setFeedbackSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/feedback/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message_index: messageIndex,
          feedback_type: 'negative',
          issues: selectedIssues,
          comment: feedbackText || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Feedback negativo guardado:', {
          feedback_id: data.feedback_id,
          issues: selectedIssues,
          comment: feedbackText
        });
      } else {
        const errorText = await response.text();
        console.error('❌ Error al guardar feedback negativo:', response.status, errorText);
      }
    } catch (err) {
      console.error('❌ Error de red al guardar feedback negativo:', err);
    } finally {
      setFeedbackSubmitting(false);
    }
    
    // Cerrar modal
    setShowFeedbackModal(false);
    setFeedbackModalData({ messageIndex: null, feedbackText: '', selectedIssues: [] });
  };

  // Función para toggle de issues en el modal
  const toggleIssue = (issue) => {
    setFeedbackModalData(prev => ({
      ...prev,
      selectedIssues: prev.selectedIssues.includes(issue)
        ? prev.selectedIssues.filter(i => i !== issue)
        : [...prev.selectedIssues, issue]
    }));
  };

  const handleSendMessage = async (e, forcedMessage = null) => {
    if (e) e.preventDefault();

    const inputMessage = typeof forcedMessage === 'string' ? forcedMessage : message;

    // Verificación robusta para prevenir doble envío
    if (!inputMessage.trim() || isSendingRef.current) return;
    if (!projectId || !datasetId) {
      alert(language === 'en' ? 'You must specify project_id and dataset_id before sending messages.' : 'Debes especificar project_id y dataset_id antes de enviar mensajes.');
      return;
    }

    let activeSessionId = sessionId;
    if (!activeSessionId) {
      try {
        const sessionResponse = await fetch(`${API_URL}/create-session`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            project_id: projectId,
            dataset_id: datasetId,
            user_id: companyInfo?.user_id || companyInfo?.company_id || 'shopify-user',
            language,
          }),
        });
        const sessionData = await sessionResponse.json();
        if (!sessionResponse.ok || !sessionData?.session_id) {
          throw new Error(sessionData?.detail || (language === 'en' ? 'Could not create session automatically' : 'No se pudo crear sesión automáticamente'));
        }
        activeSessionId = sessionData.session_id;
        setSessionId(activeSessionId);
        setCurrentConversationId(activeSessionId);
        localStorage.setItem('sessionId', activeSessionId);
        localStorage.setItem('currentConversationId', activeSessionId);
      } catch (sessionError) {
        alert(`${language === 'en' ? 'Error creating session' : 'Error creando sesión'}: ${sessionError.message}`);
        return;
      }
    }

    // Marcar como enviando inmediatamente
    isSendingRef.current = true;
    setIsLoading(true);

    const userMessage = { sender: 'user', text: inputMessage };
    setChatHistory(prev => [...prev, userMessage]);
    setMessage('');
    setThinkingMessages([]); // Limpiar thinking messages anteriores

    try {
      const response = await fetch(`${API_URL}/chat/${activeSessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, // El token de autenticación sigue en la cabecera
        },
        // Corregimos el cuerpo para que coincida con lo que el backend espera
        body: JSON.stringify({
          "content": inputMessage,
          "token": token,
          // Usar project/dataset dinámicos según el usuario actual
          "project_id": projectId,
          "dataset_id": datasetId,
          "language": language
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        // FastAPI envía un error detallado en caso de un 422. Lo formateamos para que sea legible.
        let errorMessage = errorData.detail || 'La respuesta de la red no fue OK';
        if (Array.isArray(errorMessage)) {
          errorMessage = errorMessage.map(err => `${err.loc.join(' -> ')}: ${err.msg}`).join('; ');
        }
        throw new Error(errorMessage);
      }
      
      // Como la respuesta es un stream, la procesamos línea por línea
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let textBeforeViz = ''; // Variable para detectar duplicados post-visualización
      let buffer = ''; // Buffer para acumular eventos SSE incompletos

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Procesar eventos completos (terminan en \n\n)
        const events = buffer.split('\n\n');
        
        // El último elemento puede estar incompleto, guardarlo en buffer
        buffer = events.pop() || '';
        
        // Procesar cada evento completo
        for (const event of events) {
          const lines = event.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.substring(6);
              if (jsonStr) {
                try {
                  const parsed = JSON.parse(jsonStr);
                  
                  // 0) Eventos de "thinking" (razonamiento del agente)
                  if (parsed.type === 'thinking') {
                    setThinkingMessages(prev => [...prev, {
                      id: `thinking-${Date.now()}-${Math.random()}`,
                      message: parsed.message,
                      phase: parsed.phase,
                      tool_name: parsed.tool_name,
                      timestamp: parsed.timestamp
                  }]);
                  }
                  
                  // 1) Texto en streaming - OCULTAR thinking messages cuando llega texto
                  if (parsed.text_chunk) {
                    // Limpiar mensajes de thinking cuando empieza a llegar la respuesta real
                    setThinkingMessages([]);
                    
                    accumulatedText += parsed.text_chunk;
                    
                    // Limpiar JSON de visualización del texto acumulado
                    let currentText = cleanTextFromChartJson(accumulatedText);
                    
                    // Lógica de deduplicación: Si el texto nuevo comienza con el texto que había antes de la visualización,
                    // significa que el backend está repitiendo el contexto. Lo cortamos.
                    if (textBeforeViz && currentText.startsWith(textBeforeViz)) {
                        currentText = currentText.substring(textBeforeViz.length).trim();
                    }
                    
                    const sqlQuery = parsed.sql_query || null; // ✅ Capturar SQL query
                    
                    // Solo actualizar si hay texto para mostrar (evitar burbujas vacías o solo espacios)
                    if (currentText) {
                      setChatHistory(prev => {
                        const newHistory = [...prev];
                        if (newHistory.length > 0 && newHistory[newHistory.length - 1].sender === 'bot' && !newHistory[newHistory.length - 1].viz) {
                          newHistory[newHistory.length - 1].text = currentText;
                          newHistory[newHistory.length - 1].isStreaming = true; // Flag para indicar streaming activo
                          if (sqlQuery) newHistory[newHistory.length - 1].sqlQuery = sqlQuery; // ✅ Guardar query
                        } else {
                          newHistory.push({ sender: 'bot', text: currentText, isStreaming: true, sqlQuery });
                        }
                        return newHistory;
                      });
                    }
                  }
                  // 2) Visualización JSON (tipo: visualization)
                  if (parsed.type === 'visualization') {
                    // También limpiar thinking messages cuando llega visualización
                    setThinkingMessages([]);
                    const sqlQuery = parsed.sql_query || null; // ✅ Capturar SQL query para visualizaciones
                    
                    // Guardar el texto actual como "texto previo" para detectar duplicados futuros
                    // Usamos el texto LIMPIO (sin JSON)
                    textBeforeViz = cleanTextFromChartJson(accumulatedText);
                    
                    // IMPORTANTE: Reiniciar el texto acumulado para evitar que se duplique 
                    // el texto anterior en un nuevo mensaje después de la visualización
                    accumulatedText = ''; 
                    
                    // Asegurarnos de que el mensaje de texto anterior esté limpio
                    setChatHistory(prev => {
                      const newHistory = [...prev];
                      // Buscar el último mensaje de texto del bot y limpiarlo por si acaso
                      for (let i = newHistory.length - 1; i >= 0; i--) {
                        if (newHistory[i].sender === 'bot' && !newHistory[i].viz) {
                          newHistory[i].text = cleanTextFromChartJson(newHistory[i].text);
                          break;
                        }
                      }
                      // Agregar la visualización
                      return [...newHistory, { sender: 'bot', viz: parsed, sqlQuery }];
                    });
                  }
                  // 3) Métricas de tokens (tipo: metrics)
                  if (parsed.type === 'metrics' && parsed.data) {
                    setMetrics(parsed.data);
                    console.log('Métricas recibidas:', parsed.data);
                  }
                } catch (e) {
                  console.error("Error al parsear JSON del stream:", jsonStr, e);
                }
              }
            }
          }
        }
      }
      
      // Procesar cualquier evento restante en el buffer
      if (buffer.trim()) {
        const lines = buffer.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.substring(6);
            if (jsonStr) {
              try {
                const parsed = JSON.parse(jsonStr);
                if (parsed.type === 'metrics' && parsed.data) {
                  setMetrics(parsed.data);
                }
              } catch (e) {
                console.log("Buffer final no contiene JSON válido (esto es normal):", jsonStr);
              }
            }
          }
        }
      }
      
      // Marcar el último mensaje bot como no streaming (streaming completado)
      setChatHistory(prev => {
        const newHistory = [...prev];
        if (newHistory.length > 0 && newHistory[newHistory.length - 1].sender === 'bot') {
          newHistory[newHistory.length - 1].isStreaming = false;
        }
        return newHistory;
      });

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { sender: 'bot', text: `Error: ${error.message}`, isError: true };
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      isSendingRef.current = false; // Liberar el bloqueo
    }
  };

  const handleGoldenCopilotAsk = async (prompt) => {
    const normalizedPrompt = String(prompt || '').trim();
    if (!normalizedPrompt) return;
    setCurrentView('chat');
    await handleSendMessage(null, normalizedPrompt);
  };

  const hasCompanyContext = Boolean(
    companyInfo?.company_id ||
    companyInfo?.database_name ||
    localStorage.getItem('companyId') ||
    localStorage.getItem('shopifyCompanyId') ||
    localStorage.getItem('datasetId')
  );

  // FIX (hueco de banners 2026-08-09): el sync inicial se marca 'complete'
  // antes de que el backfill historico reporte un estado visible
  // (queued/running/complete/failed) -> sin este puente el banner grande
  // desaparece y el chico todavia no aparece, dejando ~40s sin ningun
  // banner (confirmado en logs reales de produccion). Mientras dure el
  // handoff mostramos el banner grande en modo "Finalizing...".
  // Con cuidado de NO aplicar esto a logins normales de tiendas viejas
  // (backfill ya resuelto hace tiempo, o nunca tuvo backfill): solo cuenta
  // si el sync se completo EN ESTA SESION (shopifySyncJustCompletedRef) y
  // dentro de una ventana de seguridad de 90s (shopifySyncCompletedAtRef) -
  // pasado ese tiempo el banner grande se oculta igual aunque el backfill
  // siga sin reportar, para no quedar pegado infinito.
  const backfillStatusIsVisible = ['queued', 'running', 'complete', 'failed'].includes(
    backfillState.status
  );
  const inShopifySyncHandoff =
    shopifyInitialSync?.status === 'complete' &&
    shopifySyncJustCompletedRef.current &&
    shopifySyncCompletedAtRef.current &&
    (Date.now() - shopifySyncCompletedAtRef.current) < 90000 &&
    !backfillStatusIsVisible;

  return (
    <Routes>
      <Route path="/shopify/complete" element={<ShopifyComplete />} />
      <Route path="/shopify-demo" element={<ShopifyDemo />} />
      <Route path="/register" element={
        <div className={`App ${darkMode ? 'dark-mode' : 'light-mode'}`}>
          <Register />
        </div>
      } />
      <Route path="*" element={
        <div className={`App ${darkMode ? 'dark-mode' : 'light-mode'}`}>
          {!token ? (
            (hasShopifyInstallInProgress() && !shopifyHandoffTimedOut) ? (
              // FIX onboarding (2026-08-04): instalacion de Shopify en curso pero
              // el token aun no esta disponible en localStorage. Mostramos un
              // estado "Connecting..." en vez del login (que aparecia por un
              // instante en la primerisima carga de una install fresca). El
              // token se guarda un instante despues; cuando setToken/localStorage
              // lo tengan, este bloque se reemplaza por el dashboard.
              <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24,
                background: '#F5F7FB',
                color: '#121C2A',
                fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: 44,
                    height: 44,
                    border: '4px solid #E9D8FD',
                    borderTopColor: '#710FE5',
                    borderRadius: '50%',
                    margin: '0 auto 18px',
                    animation: 'upsellerate-spin 1s linear infinite'
                  }} />
                  <div style={{ color: '#710FE5', fontSize: 14, fontWeight: 800 }}>
                    Connecting to Shopify...
                  </div>
                </div>
                <style>{`
                  @keyframes upsellerate-spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            ) : (
            <div className="login-container">
              <div className="login-card">
                <div className="login-header">
                  <div className="logo">
                    <UPSELLerateLogo width={320} />
                  </div>
                  <h1>{tx('loginTitle')}</h1>
                  <p>{tx('loginSubtitle')}</p>
                </div>
                <form onSubmit={handleLogin} className="login-form">
                  <div className="input-group">
                    <label>{tx('username')}</label>
                    <input
                      type="text"
                      placeholder={tx('usernamePlaceholder')}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </div>
                  <div className="input-group">
                    <label>{tx('password')}</label>
                    <input
                      type="password"
                      placeholder={tx('passwordPlaceholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  <button type="submit" className="login-button" disabled={isLoading}>
                    {isLoading ? (
                      <><span className="spinner"></span> {tx('signingIn')}</>
                    ) : (
                      tx('signIn')
                    )}
                  </button>
                  <p className="register-link">
                    {tx('noAccount')} <Link to="/register">{tx('register')}</Link>
                  </p>
                </form>
              </div>
            </div>
            )
          ) : (
            <div className="app-shell">
          {/* LAYOUT FIX (incidente 2026-07-18): el banner NO puede ser hijo
              directo de .main-layout (display:flex row) — ahí se vuelve una
              columna gigante que absorbe media pantalla. Va en este shell en
              columna, como franja delgada ARRIBA del layout de sidebar+main. */}
          {FLOATING_CHAT_ENABLED && (
            <FloatingChatButton onClick={() => setCurrentView('chat')} />
          )}
          <div className="main-layout">
          {shopifyInitialSync &&
            shopifyInitialSync.status !== 'dismissed' &&
            (shopifyInitialSync.status !== 'complete' || inShopifySyncHandoff) && (() => {
            // showAsComplete solo es true en el estado "complete" REAL (fuera
            // de la ventana de handoff); durante el handoff seguimos
            // mostrando el layout/spinner de "en progreso" con un texto
            // distinto (ver isFinalizing mas abajo).
            const showAsComplete = shopifyInitialSync.status === 'complete' && !inShopifySyncHandoff;
            const isFinalizing = shopifyInitialSync.status === 'complete' && inShopifySyncHandoff;
            return (
            <div style={showAsComplete ? {
              position: 'fixed',
              right: 24,
              bottom: 24,
              zIndex: 1000,
              width: 'min(420px, calc(100vw - 32px))',
              background: '#fff',
              color: '#121C2A',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              boxShadow: '0 18px 44px rgba(18, 28, 42, 0.18)',
              padding: 20,
            } : {
              position: 'fixed',
              top: 28,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              width: 'min(520px, calc(100vw - 32px))',
              background: '#fff',
              color: '#121C2A',
              border: '1px solid #E5E7EB',
              borderRadius: 12,
              boxShadow: '0 18px 44px rgba(18, 28, 42, 0.18)',
              padding: 20,
            }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                {showAsComplete ? (
                  <div style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    background: '#DCFCE7',
                    color: '#15803D',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 900,
                    flex: '0 0 auto',
                  }}>
                    ✓
                  </div>
                ) : (
                  <div style={{
                    width: 34,
                    height: 34,
                    border: '3px solid #E9D8FD',
                    borderTopColor: '#710FE5',
                    borderRadius: '50%',
                    flex: '0 0 auto',
                    animation: 'upsellerate-shopify-spin 1s linear infinite',
                  }} />
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    fontWeight: 900,
                    fontSize: 16,
                    marginBottom: 8,
                    letterSpacing: '-0.02em',
                  }}>
                    {showAsComplete
                      ? 'Shopify connected'
                      : isFinalizing
                      ? 'Finalizing import…'
                      : 'Setting up your Shopify data'}
                  </div>

                  <div style={{
                    color: '#4B5563',
                    fontSize: 14,
                    lineHeight: 1.6,
                    maxWidth: 560,
                  }}>
                    {showAsComplete
                      ? 'Your Shopify data is ready.'
                      : isFinalizing
                      ? 'Almost there — wrapping up your historical data import.'
                      : 'We’re preparing your Shopify data in the background. You can keep using your dashboard while we finish importing your store data.'}
                  </div>

                  {!showAsComplete && (
                    <>
                      <div style={{
                        marginTop: 22,
                        display: 'grid',
                        gap: 10,
                        fontSize: 14,
                        color: '#374151',
                      }}>
                      <div>✓ Secure Shopify connection</div>
                      <div>✓ Workspace ready</div>
                      <div>
                        → Importing store data
                        {(shopifyInitialSync.orders || shopifyInitialSync.customers || shopifyInitialSync.products) ? (
                          <div style={{
                            marginTop: 6,
                            color: '#6B7280',
                            fontSize: 13,
                            lineHeight: 1.5,
                            fontWeight: 600,
                          }}>
                            {Number(shopifyInitialSync.orders || 0).toLocaleString()} orders ·{' '}
                            {Number(shopifyInitialSync.customers || 0).toLocaleString()} customers ·{' '}
                            {Number(shopifyInitialSync.products || 0).toLocaleString()} products loaded
                          </div>
                        ) : null}
                      </div>
                      <div>→ Preparing analytics dashboard</div>
                      </div>

                      <div style={{
                        color: '#710FE5',
                        fontSize: 13,
                        fontWeight: 800,
                        marginTop: 18,
                      }}>
                        {isFinalizing
                          ? 'Finishing up historical data import...'
                          : shopifyInitialSync.status === 'pending'
                          ? 'Importing store data in the background...'
                          : shopifyProgressMessages[shopifyProgressIndex]}
                      </div>
                    </>
                  )}
                  {showAsComplete && (
                    <button
                      type="button"
                      onClick={() => updateShopifyInitialSync({ status: 'dismissed' })}
                      style={{
                        marginTop: 14,
                        border: '1px solid #710FE5',
                        background: '#fff',
                        color: '#710FE5',
                        borderRadius: 6,
                        padding: '9px 14px',
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      Dismiss
                    </button>
                  )}
                </div>
              </div>
              <style>{`
                @keyframes upsellerate-shopify-spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
            );
          })()}
          {/* Sidebar - Diseño optimizado estilo Notion/Linear */}
          <aside className={`sidebar ${showSidebar ? 'open' : 'closed'}`}>
            {/* "New conversation" button removed 2026-07-19: chat left the nav
                (it's embedded in-context / behind the floating_chat flag), so a
                chat-session button in the sidebar no longer applies. */}

            <div className="sidebar-content">
              {/* Logo UPSELLerate — cabecera de marca del sidebar */}
              <div className="sidebar-brand">
                <img src={logoUpsellerate} alt="UPSELLerate" className="sidebar-brand-logo" />
              </div>
              {/* Navegación principal - Compacta */}
              <nav className="nav-section">
                {/* Canonical nav: Revenue Checkpoint, Revenue Plays, Revenue Impact,
                    Dashboard, Settings. Dashboard restored to the nav on 2026-07-20
                    per Alejo (it was mistakenly dropped on 2026-07-18), placed right
                    above Settings per Alejo's follow-up.
                    Chat lives behind the floating_chat flag shell.
                    Calendar is nav-gated behind calendar_enabled. */}
                <button
                  className={`nav-button ${currentView === 'checkpoint' ? 'active' : ''}`}
                  onClick={() => setCurrentView('checkpoint')}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2l2 6h6l-5 4 2 6-5-4-5 4 2-6-5-4h6z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
                  </svg>
                  <span>Revenue Checkpoint</span>
                </button>

                {!isCertificationMode && (
                  <button
                    className={`nav-button ${currentView === 'revenue' ? 'active' : ''}`}
                    onClick={() => setCurrentView('revenue')}
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                      <polygon points="10 2 12.5 7 18 8 14 12 15 18 10 15 5 18 6 12 2 8 7.5 7 10 2" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
                    </svg>
                    <span>Revenue Plays</span>
                  </button>
                )}

                <button
                  className={`nav-button ${currentView === 'myplays' ? 'active' : ''}`}
                  onClick={() => setCurrentView('myplays')}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <rect x="2" y="11" width="4" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                    <rect x="8" y="7" width="4" height="11" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                    <rect x="14" y="3" width="4" height="15" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                  <span>My Plays</span>
                </button>

                {CALENDAR_ENABLED && !isCertificationMode && (
                  <button
                    className={`nav-button ${currentView === 'calendar' ? 'active' : ''}`}
                    onClick={() => setCurrentView('calendar')}
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                      <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M7 2v4M13 2v4M3 8h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>Calendar</span>
                  </button>
                )}

                {!isCertificationMode && (
                  <button
                    className={`nav-button ${currentView === 'dashboard' ? 'active' : ''}`}
                    onClick={() => setCurrentView('dashboard')}
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                      <rect x="2" y="2" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                      <rect x="11" y="2" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                      <rect x="2" y="11" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                      <rect x="11" y="11" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                    <span>Dashboard</span>
                  </button>
                )}

                <button
                  className={`nav-button ${currentView === 'branding' ? 'active' : ''}`}
                  onClick={() => setCurrentView('branding')}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M10 6a2 2 0 100 4 2 2 0 000-4z" fill="currentColor"/>
                    <circle cx="6" cy="12" r="1" fill="currentColor"/>
                    <circle cx="13" cy="13" r="1" fill="currentColor"/>
                  </svg>
                  <span>Brand Kit</span>
                </button>

                <button
                  className={`nav-button ${currentView === 'settings' ? 'active' : ''}`}
                  onClick={() => setCurrentView('settings')}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.2 4.2l1.4 1.4M14.4 14.4l1.4 1.4M4.2 15.8l1.4-1.4M14.4 5.6l1.4-1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span>Settings</span>
                </button>

                {BILLING_UI_ENABLED && (
                <>
                {/* Standalone Plays control-only view retired from nav 2026-08-02:
                    fused into "My Plays" above (control + results). PlaysDashboard.jsx
                    kept for now (unused/orphaned), reachable only by setting
                    currentView('plays') programmatically if ever needed. */}
                <button
                  className={`nav-button ${currentView === 'pricing' ? 'active' : ''}`}
                  onClick={() => setCurrentView('pricing')}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2v16M14 5.5c0-1.4-1.6-2.5-4-2.5s-4 1.1-4 2.5 1.6 2.2 4 2.5c2.4.3 4 1.1 4 2.5s-1.6 2.5-4 2.5-4-1.1-4-2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Plans</span>
                </button>
                </>
                )}
        </nav>
              {/* Separador visual entre navegación y conversaciones */}
              {currentView === 'chat' && conversations.length > 0 && (
                <div className="sidebar-divider"></div>
              )}

              {/* Lista de conversaciones con agrupación por fecha */}
              {currentView === 'chat' && conversations.length > 0 && (
                <div className="conversations-list">
                  <div className="conversations-header">
                    <span className="conversations-title">{tx('conversations')}</span>
                    {sessionId && (
                      <span className="session-indicator" title={`${tx('sessionTitle')}: ${sessionId.substring(0, 8)}...`}>
                        <span className="status-dot-mini"></span>
                      </span>
                    )}
                  </div>
                  
                  {/* Grupo: Hoy */}
                  {conversations.filter(c => {
                    const date = new Date(c.created_at || c.timestamp);
                    const today = new Date();
                    return date.toDateString() === today.toDateString();
                  }).length > 0 && (
                    <div className="conversation-group">
                      <div className="conversation-group-label">{tx('today')}</div>
                      {conversations.filter(c => {
                        const date = new Date(c.created_at || c.timestamp);
                        const today = new Date();
                        return date.toDateString() === today.toDateString();
                      }).map((conv) => (
                        <div
                          key={conv.session_id}
                          className={`conversation-item ${currentConversationId === conv.session_id ? 'active' : ''}`}
                          onClick={() => switchConversation(conv.session_id)}
                        >
                          <svg className="conversation-icon" width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <path d="M14 10c0 .6-.4 1-1 1h-8l-3 3V3c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v7z" stroke="currentColor" strokeWidth="1.5"/>
                          </svg>
                          <span className="conversation-title">{conv.title}</span>
                          <button
                            className="delete-conversation"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteConversation(conv.session_id);
                            }}
                            title={tx('deleteConversation')}
                          >
                            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                              <path d="M1 3.5h12M5.5 1h3M11.5 3.5v9a1 1 0 01-1 1h-7a1 1 0 01-1-1v-9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Grupo: Ayer */}
                  {conversations.filter(c => {
                    const date = new Date(c.created_at || c.timestamp);
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    return date.toDateString() === yesterday.toDateString();
                  }).length > 0 && (
                    <div className="conversation-group">
                      <div className="conversation-group-label">{tx('yesterday')}</div>
                      {conversations.filter(c => {
                        const date = new Date(c.created_at || c.timestamp);
                        const yesterday = new Date();
                        yesterday.setDate(yesterday.getDate() - 1);
                        return date.toDateString() === yesterday.toDateString();
                      }).map((conv) => (
                        <div
                          key={conv.session_id}
                          className={`conversation-item ${currentConversationId === conv.session_id ? 'active' : ''}`}
                          onClick={() => switchConversation(conv.session_id)}
                        >
                          <svg className="conversation-icon" width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <path d="M14 10c0 .6-.4 1-1 1h-8l-3 3V3c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v7z" stroke="currentColor" strokeWidth="1.5"/>
                          </svg>
                          <span className="conversation-title">{conv.title}</span>
                          <button
                            className="delete-conversation"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteConversation(conv.session_id);
                            }}
                            title={tx('deleteConversation')}
                          >
                            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                              <path d="M1 3.5h12M5.5 1h3M11.5 3.5v9a1 1 0 01-1 1h-7a1 1 0 01-1-1v-9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Grupo: Anteriores */}
                  {conversations.filter(c => {
                    const date = new Date(c.created_at || c.timestamp);
                    const today = new Date();
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    return date.toDateString() !== today.toDateString() && date.toDateString() !== yesterday.toDateString();
                  }).length > 0 && (
                    <div className="conversation-group">
                      <div className="conversation-group-label">{tx('previous')}</div>
                      {conversations.filter(c => {
                        const date = new Date(c.created_at || c.timestamp);
                        const today = new Date();
                        const yesterday = new Date();
                        yesterday.setDate(yesterday.getDate() - 1);
                        return date.toDateString() !== today.toDateString() && date.toDateString() !== yesterday.toDateString();
                      }).map((conv) => (
                        <div
                          key={conv.session_id}
                          className={`conversation-item ${currentConversationId === conv.session_id ? 'active' : ''}`}
                          onClick={() => switchConversation(conv.session_id)}
                        >
                          <svg className="conversation-icon" width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <path d="M14 10c0 .6-.4 1-1 1h-8l-3 3V3c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v7z" stroke="currentColor" strokeWidth="1.5"/>
                          </svg>
                          <span className="conversation-title">{conv.title}</span>
                          <button
                            className="delete-conversation"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteConversation(conv.session_id);
                            }}
                            title={tx('deleteConversation')}
                          >
                            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                              <path d="M1 3.5h12M5.5 1h3M11.5 3.5v9a1 1 0 01-1 1h-7a1 1 0 01-1-1v-9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer compacto y alineado */}
            <div className="sidebar-footer">
              {/* Fila de controles */}
              <div className="footer-controls">
                {/* Selector de idioma compacto */}
                <div className="language-select-wrapper">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3"/>
                    <path d="M2 8h12M8 2c1.5 1.5 2.5 3.5 2.5 6s-1 4.5-2.5 6c-1.5-1.5-2.5-3.5-2.5-6s1-4.5 2.5-6z" stroke="currentColor" strokeWidth="1.3"/>
                  </svg>
                  <select 
                    className="language-select" 
                    value={language} 
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    <option value="es">ES</option>
                    <option value="en">EN</option>
                  </select>
                </div>
                
                {/* Toggle métricas */}
                <button 
                  className={`footer-icon-btn ${showMetricsPanel ? 'active' : ''}`}
                  onClick={() => setShowMetricsPanel(!showMetricsPanel)}
                  title={showMetricsPanel ? tx('hideMetrics') : tx('showMetrics')}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="2" y="10" width="3" height="4" rx="1" fill="currentColor"/>
                    <rect x="6.5" y="6" width="3" height="8" rx="1" fill="currentColor"/>
                    <rect x="11" y="2" width="3" height="12" rx="1" fill="currentColor"/>
                  </svg>
                </button>
                
              </div>

              {/* Nombre de empresa */}
              {companyInfo?.company_name && (
                <div className="sidebar-company">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span>{companyInfo.company_name}</span>
                </div>
              )}

              {/* Botón logout compacto */}
              <button 
                className="logout-btn"
                onClick={() => {
                  localStorage.removeItem('token');
                  clearStoredSessionContext();
                  setToken(null);
                  resetAuthenticatedContextState();
                }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="logout-text">{tx('logout')}</span>
              </button>
            </div>
          </aside>

          {/* Main Chat Area */}
          <main className="chat-main">
            {/* Renderizar Dashboard, Integraciones o Chat según la vista activa */}
            {currentView === null ? (
              <div style={{ padding: '48px', color: '#6b7280' }}>
                Preparing your workspace...
              </div>
            ) : currentView === 'revenue' ? (
              <RevenueFinder
                companyInfo={companyInfo}
                token={token}
                onNavigate={(view) => setCurrentView(view)}
                onAskAI={() => setCurrentView('chat')}
              />
            ) : currentView === 'dashboard' ? (
              <Dashboard
                key={`dashboard-${dashboardReloadKey}`}
                companyInfo={companyInfo}
                shopifyInitialSync={shopifyInitialSync}
              />
            ) : currentView === 'integrations' ? (
              <Integrations companyInfo={companyInfo} />
            ) : currentView === 'impact' ? (
              <ImpactPage
                companyInfo={companyInfo}
                token={token}
                onNavigate={(view) => setCurrentView(view)}
              />
            ) : currentView === 'myplays' ? (
              <MyPlays
                companyInfo={companyInfo}
                token={token}
                onNavigate={(view) => setCurrentView(view)}
              />
            ) : currentView === 'checkpoint' ? (
              <RevenueCheckpoint
                companyInfo={companyInfo}
                token={token}
                onNavigate={(view) => setCurrentView(view)}
                onBackfillStatus={(status, hasData) =>
                  setBackfillState((current) => ({
                    ...current,
                    status,
                    hasData,
                  }))
                }
              />
            ) : currentView === 'branding' ? (
              <EmailBranding companyInfo={companyInfo} token={token} />
            ) : currentView === 'settings' ? (
              <Settings
                companyInfo={companyInfo}
                token={token}
                onNavigate={(view) => setCurrentView(view)}
                apiUrl={DASHBOARD_API_URL}
              />
            ) : currentView === 'pricing' && BILLING_UI_ENABLED ? (
              <PlaysView
                token={token}
                checkoutResult={billingCheckoutResult}
                onDismissCheckoutResult={() => setBillingCheckoutResult(null)}
              />
            ) : currentView === 'plays' && BILLING_UI_ENABLED ? (
              <PlaysDashboard
                token={token}
                onNavigateToPricing={() => setCurrentView('pricing')}
              />
            ) : currentView === 'admin' ? (
              // Internal billing admin screen. Intentionally NOT in the nav:
              // reached only by navigating to /admin (captured on mount above)
              // or by setting currentView programmatically. Auth is its own
              // BILLING_ADMIN_TOKEN prompt inside the component.
              <AdminAccounts />
            ) : currentView === 'opportunities' ? (
              <div className="chat-container opportunities-container">
                <GoldenOpportunities onAskCopilot={handleGoldenCopilotAsk} />
              </div>
            ) : currentView === 'calendar' ? (
              <div className="chat-container opportunities-container">
                <CalendarioCampanas onAskChat={handleGoldenCopilotAsk} />
              </div>
            ) : (
              <>
            <header className="chat-header">
              <button className="sidebar-toggle" onClick={() => setShowSidebar(!showSidebar)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <div className="header-logo-title">
                <UPSELLerateLogo width={160} />
              </div>
              <div className="header-actions">
                {metrics && (
                  <div className="quick-metrics">
                    <span className="metric-badge">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{marginRight: '4px'}}>
                        <rect x="1" y="1" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M4 7h6M7 4v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      {metrics.total_tokens?.toLocaleString() || 0}
                    </span>
                    <span className="metric-badge">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{marginRight: '4px'}}>
                        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M7 3.5v7M5.5 5h2c.8 0 1.5.7 1.5 1.5S8.3 8 7.5 8h-1c-.8 0-1.5.7-1.5 1.5S5.7 11 6.5 11h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                      </svg>
                      ${(metrics.cost_estimate_usd || metrics.total_tokens * 0.00004).toFixed(3)}
                    </span>
                  </div>
                )}
              </div>
            </header>

            {/* Panel de Métricas Expandido */}
            {showMetricsPanel && metrics && (
              <div className="metrics-panel-expanded">
                <div className="metrics-grid-compact">
                  <div className="metric-card-compact primary">
                    <span className="metric-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <rect x="2" y="2" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M6 10h8M10 6v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                    <div>
                      <div className="metric-value">{metrics.total_tokens?.toLocaleString() || 0}</div>
                      <div className="metric-label">{tx('totalTokens')}</div>
                    </div>
                  </div>
                  <div className="metric-card-compact success">
                    <span className="metric-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M10 5v10M7 8h3c1.1 0 2 .9 2 2s-.9 2-2 2H8c-1.1 0-2 .9-2 2s.9 2 2 2h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                    <div>
                      <div className="metric-value">
                        ${(metrics.cost_estimate_usd || metrics.total_tokens * 0.00004).toFixed(4)}
                      </div>
                      <div className="metric-label">{tx('costUsd')}</div>
                    </div>
                  </div>
                  <div className="metric-card-compact info">
                    <span className="metric-icon">
                      <BotIcon />
                    </span>
                    <div>
                      <div className="metric-value">{metrics.llm_calls || 0}</div>
                      <div className="metric-label">{tx('llmCalls')}</div>
                    </div>
                  </div>
                  <div className="metric-card-compact warning">
                    <span className="metric-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.7 7.3l-1.4-1.4M10 3v2M3 10h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M16 10c0 3.3-2.7 6-6 6s-6-2.7-6-6 2.7-6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="10" cy="10" r="2" fill="currentColor"/>
                      </svg>
                    </span>
                    <div>
                      <div className="metric-value">{metrics.tool_calls || 0}</div>
                      <div className="metric-label">{tx('tools')}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="chat-container">
              {chatHistory.length === 0 && !isLoading ? (
                <div className="welcome-screen">
                  <div className="welcome-icon">
                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                      <circle cx="40" cy="40" r="36" fill="url(#welcomeGradient)" opacity="0.2"/>
                      <path d="M30 40L35 45L50 30" stroke="url(#welcomeGradient)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                      <defs>
                        <linearGradient id="welcomeGradient" x1="0" y1="0" x2="80" y2="80">
                          <stop offset="0%" stopColor="#8b5cf6" />
                          <stop offset="100%" stopColor="#a78bfa" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <h2>{tx('welcomeTitle')}</h2>
                  <p>{tx('welcomeSubtitle')}</p>
                  <div className="suggestion-cards">
                    <button className="suggestion-card" onClick={() => setMessage(tx('suggestionSalesPrompt'))}>
                      <span className="suggestion-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <rect x="2" y="14" width="3" height="4" rx="1" fill="currentColor"/>
                          <rect x="7" y="9" width="3" height="9" rx="1" fill="currentColor"/>
                          <rect x="12" y="5" width="3" height="13" rx="1" fill="currentColor"/>
                        </svg>
                      </span>
                      <span>{tx('suggestionSalesLabel')}</span>
                    </button>
                    <button className="suggestion-card" onClick={() => setMessage(tx('suggestionSegmentPrompt'))}>
                      <span className="suggestion-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <circle cx="7" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
                          <circle cx="14" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M2 16c0-2.5 2-4.5 5-4.5s5 2 5 4.5M12 16c0-2 1.5-3.5 4-3.5s4 1.5 4 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </span>
                      <span>{tx('suggestionSegmentLabel')}</span>
                    </button>
                    <button className="suggestion-card" onClick={() => setMessage(tx('suggestionTopPrompt'))}>
                      <span className="suggestion-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M10 2L12 7h5l-4 4 2 5-5-3-5 3 2-5-4-4h5l2-5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span>{tx('suggestionTopLabel')}</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="chat-history">
                  {chatHistory.map((msg, index) => (
                    <div key={`msg-${index}-${msg.text?.substring(0, 20) || 'viz'}`} className={`message-wrapper ${msg.sender}`}>
                      <div className={`message ${msg.sender} ${msg.isError ? 'error' : ''}`}>
                        <div className="message-avatar">
                          {msg.sender === 'user' ? (
                            <div className="avatar user-avatar">U</div>
                          ) : (
                            <div className="avatar bot-avatar">
                              <BotIcon />
                            </div>
                          )}
                        </div>
                        <div className={`message-content ${msg.isStreaming ? 'streaming' : ''}`}>
                          {msg.viz ? (
                            <EChartRenderer viz={msg.viz} />
                          ) : (
                            <MessageContent text={msg.text} />
                          )}
                          
                          {/* SQL Query Viewer - solo si el mensaje tiene query */}
                          {msg.sender === 'bot' && msg.sqlQuery && (() => {
                            const backendIndex = chatHistory.slice(0, index + 1).filter(m => m.text !== undefined).length - 1;
                            const isExpanded = expandedQueries[backendIndex];
                            
                            return (
                              <div className="sql-query-section">
                                <button 
                                  className="sql-query-toggle"
                                  onClick={() => toggleQueryExpansion(backendIndex)}
                                  title={isExpanded ? tx('hideSql') : tx('showSql')}
                                >
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M2 5L7 10L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                      style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transformOrigin: 'center', transition: 'transform 0.2s' }}
                                    />
                                  </svg>
                                  <span>{tx('sqlUsed')}</span>
                                </button>
                                
                                {isExpanded && (
                                  <div className="sql-query-content">
                                    <div className="sql-query-header">
                                      <span className="sql-badge">SQL</span>
                                      <button 
                                        className="sql-copy-btn"
                                        onClick={() => handleCopyQuery(msg.sqlQuery)}
                                        title={tx('copyQuery')}
                                      >
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                          <rect x="4" y="4" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                                          <path d="M2 10V2C2 1.44772 2.44772 1 3 1H9" stroke="currentColor" strokeWidth="1.5"/>
                                        </svg>
                                        {tx('copy')}
                                      </button>
                                    </div>
                                    <pre className="sql-query-code">
                                      <code>{msg.sqlQuery}</code>
                                    </pre>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          
                          {/* Botones de feedback solo para mensajes del bot */}
                          {msg.sender === 'bot' && !msg.isError && (() => {
                            // Calcular índice real en el backend (contar solo mensajes con texto)
                            const backendIndex = chatHistory.slice(0, index + 1).filter(m => m.text !== undefined).length - 1;
                            
                            return (
                              <div className="message-actions">
                                <button 
                                  className="action-button" 
                                  onClick={() => handleCopyMessage(backendIndex, msg.text)}
                                  title={tx('copy')}
                                >
                                  {copiedMessageIndex === backendIndex ? (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  ) : (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <rect x="5" y="5" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                                      <path d="M3 11V3C3 2.44772 3.44772 2 4 2H10" stroke="currentColor" strokeWidth="1.5"/>
                                    </svg>
                                  )}
                                </button>
                                <button 
                                  className={`action-button ${messageFeedback[backendIndex] === 'positive' ? 'active' : ''}`}
                                  onClick={() => handlePositiveFeedback(backendIndex)}
                                  title={tx('goodAnswer')}
                                >
                                  <ThumbsUpIcon />
                                </button>
                                <button 
                                  className={`action-button ${messageFeedback[backendIndex] === 'negative' ? 'active' : ''}`}
                                  onClick={() => handleNegativeFeedback(backendIndex)}
                                  title={tx('badAnswer')}
                                >
                                  <ThumbsDownIcon />
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Mensajes de "thinking" (razonamiento del agente) */}
                  {thinkingMessages.length > 0 && (
                    <div className="message-wrapper bot">
                      <div className="message bot thinking-message">
                        <div className="message-avatar">
                          <div className="avatar bot-avatar">
                            <BotIcon />
                          </div>
                        </div>
                        <div className="message-content thinking-content">
                          {thinkingMessages.slice(-3).map((thinking) => (
                            <div 
                              key={thinking.id} 
                              className="thinking-step"
                              data-phase={thinking.phase}
                            >
                              {thinking.message}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {isLoading && thinkingMessages.length === 0 && (
                    <div className="message-wrapper bot">
                      <div className="message bot">
                        <div className="message-avatar">
                          <div className="avatar bot-avatar">
                            <BotIcon />
                          </div>
                        </div>
                        <div className="message-content">
                          <div className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              )}
            </div>

            <div className="chat-input-container">
              <form onSubmit={handleSendMessage} className="chat-input-form">
                <div className="input-wrapper">
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={sessionId ? tx('messagePlaceholder') : tx('startSessionPlaceholder')}
                    disabled={(!sessionId && !hasCompanyContext) || isLoading}
                    rows="1"
                  />
                  <button 
                    type="submit" 
                    className="send-button"
                    disabled={((!sessionId && !hasCompanyContext) || isLoading || !message.trim())}
                  >
                    {isLoading ? (
                      <span className="spinner-small"></span>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M18 2L9 11M18 2L12 18L9 11M18 2L2 8L9 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
                <div className="input-footer">
                  <small>{tx('enterHint')}</small>
                </div>
              </form>
            </div>
              </>
            )}
          {/* Backfill floating banner — Estado 2 & 3 */}
          {(
            ['queued', 'running'].includes(backfillState.status)
          ) && (
            <div className="backfill-banner">
              <p className="backfill-banner-title">
                <span className="backfill-banner-spinner" aria-hidden="true"></span>
                Historical data import in progress
              </p>

              <div className="backfill-banner-stats">
                  <div>
                    <strong>{Number(backfillState.orders || 0).toLocaleString()}</strong>
                    <span>orders</span>
                  </div>
                  <div>
                    <strong>{Number(backfillState.customers || 0).toLocaleString()}</strong>
                    <span>customers</span>
                  </div>
                  <div>
                    <strong>{Number(backfillState.products || 0).toLocaleString()}</strong>
                    <span>products</span>
                  </div>
                  <div>
                    <strong>{Number(backfillState.collections || 0).toLocaleString()}</strong>
                    <span>collections</span>
                  </div>
                </div>

              <p className="backfill-banner-text">
                You can continue using the dashboard while we finish importing your historical data.
              </p>
            </div>
          )}
          </main>
          </div>
        </div>
      )}

      {/* Modal de Feedback Negativo */}
      {showFeedbackModal && (
        <div className="feedback-modal-overlay" onClick={() => setShowFeedbackModal(false)}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h3>{tx('feedbackTitle')}</h3>
              <button className="modal-close-btn" onClick={() => setShowFeedbackModal(false)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            
            <div className="feedback-modal-body">
              <p className="feedback-modal-subtitle">{tx('feedbackIssueQuestion')}</p>
              
              <div className="feedback-issues">
                {[
                  { id: 'incorrect', label: language === 'en' ? 'Incorrect information' : 'Información incorrecta' },
                  { id: 'incomplete', label: language === 'en' ? 'Incomplete answer' : 'Respuesta incompleta' },
                  { id: 'confusing', label: language === 'en' ? 'Confusing or unclear' : 'Confusa o poco clara' },
                  { id: 'irrelevant', label: language === 'en' ? 'Does not answer the question' : 'No responde la pregunta' },
                  { id: 'format', label: language === 'en' ? 'Format or presentation' : 'Formato o presentación' },
                  { id: 'other', label: language === 'en' ? 'Other' : 'Otro' }
                ].map(issue => (
                  <button
                    key={issue.id}
                    className={`issue-chip ${feedbackModalData.selectedIssues.includes(issue.id) ? 'selected' : ''}`}
                    onClick={() => toggleIssue(issue.id)}
                  >
                    {issue.label}
                  </button>
                ))}
              </div>

              <p className="feedback-modal-subtitle" style={{ marginTop: '20px' }}>{tx('feedbackDetailQuestion')}</p>
              <textarea
                className="feedback-textarea"
                placeholder={tx('feedbackPlaceholder')}
                value={feedbackModalData.feedbackText}
                onChange={(e) => setFeedbackModalData(prev => ({ ...prev, feedbackText: e.target.value }))}
                rows="4"
              />

              <div className="feedback-modal-footer">
                <button className="feedback-btn-cancel" onClick={() => setShowFeedbackModal(false)}>
                  {tx('cancel')}
                </button>
                <button className="feedback-btn-submit" onClick={handleSubmitNegativeFeedback}>
                  {tx('sendFeedback')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      } />
    </Routes>
  );
}

export default App;
